import { SecurityQuestionMetaMap, SecurityIntroInstructionMap } from '../models/form-definition';

export const SITE_SECURITY_INTRO_INSTRUCTIONS: SecurityIntroInstructionMap = {
  "security_management_intro": "Security management refers to the people, plans, and procedures that a K-12 school has in place to deal with security issues, including but not limited to active-shooter issues. Factors that contribute to the effectiveness of security and emergency management efforts at schools include the designation of a security manager, existence of security and emergency operations plans; commitment to training and exercises on these plans; procedures for handling suspicious objects; and engaging in preparedness and security working groups with external partners. Schools may use different names for a security plan or incorporate elements of a security plan into broader emergency operations plans, which outline the school's approach to operations before, during, and after an emergency. Understanding which activities occur at an individual school level and which activities occur at the school district level are important considerations for K-12 school personnel to factor into their security and emergency management practices.",
  "security_force_intro": "A security force is a group of school employees or contractors whose sole responsibilities are to provide security at a school. A security force does not include general school personnel who are trained in security awareness (i.e., observe and report) in addition to their regular duties. Security forces at schools may include school resource officers (SROs), who are sworn law enforcement officers responsible for safety and crime prevention in schools. A local police department, sheriff's agency, or school system typically employs SROs who work closely with school administrators in an effort to create a safer environment. The responsibilities of SROs are similar to regular police officers in that they have the ability to make arrests, respond to calls for service, and document incidents that occur within their jurisdiction. Some schools may have a dedicated SRO who is assigned full-time to an individual school. Other schools may have SROs who balance responsibilities at multiple schools within a district. Other models may involve full- or part-time private security personnel serving in security force roles, or school officials serving in multiple roles that include but are not limited to security. In this section, a school is considered to have security force only if it has people whose sole responsibilities are to provide security.",
  "entry_control": "Controlling how and when faculty, staff, students, and visitors can access school buildings and grounds is considered an effective mechanism for protecting against different threats, including active shooters. These controls can include minimizing the number of points of entry, requiring identification, or conducting searches. However, these entry controls may sometimes run counter to the overarching objective of creating an open learning environment at K-12 schools. Entry controls are part of the broader layers of defense that schools have in place to enhance security. These layers of protective measures are deployed in concentric circles around a school, starting at the outer perimeter and moving inward to areas with the greatest need for protection. Entry controls can help deter individuals from initiating violent attacks, detect attacks earlier at a safe distance, and delay attackers from reaching vulnerable and/or highly populated locations.",
  "fencing_and_gates_intro": "Fences are barriers enclosing or bordering a school that are used to prevent entrance, contain people to particular areas, or mark a boundary. Gates are openings in that perimeter that allow people or vehicles to pass through at controlled points of entry. Together, fences and gates are part of the broader layers of defense that schools have in place to protect against a spectrum of security issues, including but not limited to active shooters. These layers of protective measures are deployed in concentric circles around a school, starting at the outer perimeter and moving inward to areas with the greatest need for protection. Often fences are installed at a school for the purpose of protecting people and property from harm or damage from playground or sports items. These sections of fence may serve dual purposes of security and protection. However, when looking at fence lines, school officials should consider all sections of the fence, not just the tallest, newest, or what appears to be most secure. Areas where a person could easily penetrate the fence line and access the property are also important to evaluate. Fences and gates may deter or delay active-shooter intrusion. Fence construction may include different materials (e.g., chain link, wood, wrought iron, plastic), heights, anchoring, and other features (e.g., barbed wire along the top, privacy screening, outriggers). Similarly, gates can apply to vehicles and pedestrians and may include moveable bollards, roller or slide gates, swing gates, or turnstiles, among other construction options. It is understood that use of fences or gates is simply not practical in many locations. In that case, the building envelope, in particular windows and doors and the entry control process are the primary elements to deter or delay.",
  "parking_and_barriers_intro": "Parking and barriers are part of the broader layers of defense that schools have in place to enhance security. These security measures may be considered more relevant to explosive threats (i.e., where standoff distance is important) or vehicle ramming threats (i.e., where high-speed avenues of approach are a concern). However, parking controls and barriers can also help deter individuals from initiating armed attacks; detect these attacks earlier at a safe distance; and delay attackers from reaching vulnerable and/or highly populated locations on school grounds. Monitoring parking areas for suspicious or illegal vehicle placement can include viewing the parking area via security cameras, requiring onsite security personnel to conduct patrols, or maintaining incidental visual contact through windows. Vehicle screening processes may also uncover weapons that individuals plan to use in active-shooter attacks.",
  "building_envelope_intro": "Building envelope is the exterior face of a school building, including walls, roof, windows, and doors. The building envelope provides a significant layer of defense but also includes notable vulnerabilities (i.e., doors, windows) that are important to consider for physical security. Construction materials for doors and windows in particular influence the effectiveness of these features in deterring, delaying, or denying active-shooter attacks. For doors, options include metal- or wood-framed glass; solid- or hollow-core wood; fire-rated steel or aluminum; hollow steel; metal clad; or blast-resistant. Windows may include tempered glass; wire-reinforced glass; laminated glass; bullet-proof glass; and blast-resistant safety films. Access to utilities and fire alarms are important physical security considerations in active-shooter situations because attackers can coopt these features to create diversions or complicate response efforts. Portable buildings that are used for classrooms often add unique challenges. Portable buildings or temporary structures used for classrooms often do not have the same construction features as the primary building. Thus, windows and doors will not have the same level of security. Often the portable buildings are located in unsecured areas that provide easy access to an attacker. If a school has portable buildings and eliminating their use is not practical, additional security measures are often necessary, including increased monitoring, assigned security personnel, retrofitting doors and locks, or ballistic protection on the windows. Securing these school building features as part of steady-state efforts may deter attackers from attempting to exploit them.",
  "ccv_vss_intro": "CCV and video surveillance systems (VSS) are electronic systems of cameras, control equipment, recorders, and related apparatus used for surveillance or alarm assessment. These systems can help deter individuals from initiating armed attacks and detect these attacks earlier at a safe distance. CCV/VSS technology options include a range of technologies (i.e., digital or analog, fiber or wireless transmission) and features (i.e., color or black-and-white video, adjustable side-to-side or up-and-down movement of cameras, wide-angle or zoom views). They also can include software that helps identify anomalies and ultimately the ability of users to identify suspicious behaviors. Schools may have dedicated security staff who monitor these systems in real time, or they may only view recorded information in response to specific incidents or inquiries. Following an incident, CCV/VSS data can provide valuable forensic information that first responders can use in response efforts and follow-on investigations. Surveillance cameras can be used to monitor common areas that are not within the normal view of teachers, administrators, or security personnel. Video surveillance can also streamline access control procedures, allowing administrative or security personnel to monitor and control locked entrances remotely when used with intercoms and remote control door locks, if practical."
};

export const SITE_SECURITY_QUESTION_META: SecurityQuestionMetaMap = {
  "security": {
    "key": "security",
    "prompt": "Does the school have a security manager or security department?",
    "options": [
      {
        "value": "Very_Low",
        "label": "Neither the school nor the district has a security manager or department."
      },
      {
        "value": "Low",
        "label": "The school district has a security manager who also performs other functions in addition to security management."
      },
      {
        "value": "Medium",
        "label": "The school district has a security manager who has no other additional duties."
      },
      {
        "value": "High",
        "label": "The school district has a security management department. Multiple employees are dedicated to security management at the district level, but no one within the department is assigned to manage security specifically at the school in question."
      },
      {
        "value": "Very_High",
        "label": "The school has a dedicated security manager or security department."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Designate an employee to act as a security manager, and ensure that person is responsible for developing, implementing, and coordinating all security-related activities. Provide some initial training to the appointed security manager.",
      "Low": "Relieve the manager of other responsibilities unrelated to school safety and security.",
      "Medium": "Hire additional personnel to support the school district's security mission.",
      "High": "Assess the need to designate an employee(s) to ensuring security specifically at this school. Coordinate with the district based on the findings of this needs assessment.",
      "Very_High": "Regularly assess the school's security management program. Implement best practices based on collaborative relationships and new or updated resources."
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "items": [
              {
                "text": "Security managers are responsible for the effective implementation of security policies, programs, directives, and training within their school district or school. Security departments are a group of people working together to fulfill this same purpose."
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "items": [
              {
                "text": "Interagency Security Committee (ISC), Facility Security Plan: An Interagency Security Committee Guide, 1st Edition, February 2015, accessed April 24, 2018"
              },
              {
                "link": "https://www.cisa.gov/sites/default/files/publications/ISC-Facility-Security-Plan-Guide-2015-508.pdf"
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "Lawrence J. Fennelly and Marianna A. Perry, The Handbook for School Safety and Security: Best Practices and Procedures (Amsterdam, The Netherlands: Elsevier 2014)."
              }
            ]
          }
        ]
      }
    ]
  },
  "threat_assessment": {
    "key": "threat_assessment",
    "prompt": "Does the school have a threat assessment team to respond to behavior that raises safety concerns?",
    "options": [
      {
        "value": "Very_Low",
        "label": "The school does not have a threat assessment team. School personnel do not recognize a need to establish a formal process for assessing potential threats."
      },
      {
        "value": "Low",
        "label": "The school has engaged in activities that indicate it recognizes the need to assess potential threats, but it has not established a threat assessment team. School personnel may have developed checklists or proposed forming a threat assessment program."
      },
      {
        "value": "Medium",
        "label": "The school has established a threat assessment team. Team members may be limited to school administrators, faculty, and/or staff. They have received initial training on the threat assessment process. Written documentation of the threat assessment process exists, but it may be incomplete. The team may not meet regularly."
      },
      {
        "value": "High",
        "label": "The school has a multidisciplinary threat assessment team that includes community partners. Team members have received extensive training on the threat assessment process. Comprehensive written plans and procedures guide the threat assessment team. The team meets regularly. The school has communicated information about the threat assessment policy to faculty, staff, students, and families, but threatening behavior generally remains unreported. "
      },
      {
        "value": "Very_High",
        "label": "The school has a multidisciplinary threat assessment team that includes community partners. Team members receive annual training on the threat assessment process. Comprehensive written plans and procedures guide the threat assessment team. The team meets regularly. Written materials about the team’s purpose are widely available, and the threat assessment policy has been explained to faculty, staff, students, and families. The threat assessment team receives reports about threatening behavior and investigates accordingly. "
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Assign a staff or faculty member the responsibility of researching and determining the value for the school of a threat assessment team. Visit the U.S. Department of Education's (ED's) Readiness and Emergency Management for Schools (REMS) Technical Assistance (TA) Center website for information and assistance on forming a threat assessment team.",
      "Low": "Establish a team to review threatening behavior. Provide training on the threat assessment process. Develop written policies and procedures to guide the team's activities.",
      "Medium": "Expand the threat assessment team to include community partners, such as local law enforcement. Provide additional training to team members. Establish comprehensive guidance documents, and set up a schedule for the threat assessment team to meet regularly.",
      "High": "Raise community awareness of the threat assessment team's purpose. Provide materials to explain the threat assessment policy to faculty, staff, students, and families. Provide clear direction on the preferred methods for reporting potential threats. Reassure students their reports will remain confidential and anonymous.",
      "Very_High": "Continue and expand efforts to ensure the threat assessment process remains relevant in the school community. Regularly review the outcomes associated with reported threats to improve the overall threat assessment process. Identify new means (technological or otherwise) to encourage and facilitate reporting."
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "items": [
              {
                "text": "A school threat assessment team analyzes communication and behaviors to determine whether a student, faculty or staff member, or other individual may pose a threat. The team serves as a central convening body so that warning signs that multiple people observe and report are not dismissed as isolated incidents when they actually may represent escalating behavior that is a serious concern. Threat assessment team members should include school principals, counselors, employees, medical and mental health professionals, law enforcement personnel, and school resource officers (SROs), where applicable."
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "items": [
              {
                "text": "ED, U.S. Department of Health and Human Services (HHS), DHS, and U.S. Department of Justice (DOJ), Guide for Developing High-Quality School Emergency Operations Plans, 2013, accessed April 24, 2018"
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "”Threat Assessment Information,” Virginia Tech Police Department"
              },
              {
                "link": "https://threatassessment.vt.edu/resources/tat-info.html"
              }
            ]
          },
          {
            "items": [
              {
                "text": "Robert A. Fein, et al., Threat Assessment in Schools: A Guide to Managing Threatening Situations and to Creating Safe School Climates, July 2004"
              },
              {
                "link": "https://www.ed.gov/admins/lead/safety/threatassessmentguide.pdf",
                "text": "(link)"
              }
            ]
          },
          {
            "items": [
              {
                "text": "REMS Technical Assistance TA Center,” ED, Office of Safe and Healthy Students "
              },
              {
                "link": "https://rems.ed.gov"
              }
            ]
          },
          {
            "items": [
              {
                "text": "Donna Michaelis, et al., “Forming a School Behavioral Threat Assessment Team,” REMS TA Center, August 26, 2016 "
              },
              {
                "link": "https://rems.ed.gov/ThreatAssessmentTeam2016Webinar.aspx#.Mia"
              }
            ]
          },
          {
            "items": [
              {
                "text": "Langheim and Paul Myers, Director of Research and Development, “Use of Social Media in School Behavioral Threat Assessments,” REMS TA Center, September 2, 2016"
              },
              {
                "link": "https://rems.ed.gov/SocialMediaThreatAssessment2016Webinar.aspx"
              }
            ]
          },
          {
            "items": [
              {
                "text": "Sarah Goodrum and William Woodward, Report on the Arapahoe High School Shooting: Lessons Learned on Information Sharing, Threat Assessment, and Systems Integrity, January 18, 2016"
              },
              {
                "link": "http://cdpsdocs.state.co.us/safeschools/Resources/AHS-reports/CSPV.AHSFullReport.pdf"
              }
            ]
          },
          {
            "items": [
              {
                "text": "Mary Ellen O'Toole, The School Shooter: A Threat Assessment Perspective, Federal Bureau of Investigation (FBI), 1999"
              },
              {
                "link": "https://www.fbi.gov/file-repository/stats-services-publications-school-shooter-school-shooter/view"
              }
            ]
          },
          {
            "items": [
              {
                "text": "ISC, Planning and Response to an Active Shooter: An Interagency Security Committee Policy and Best Practices Guide, November 2015"
              },
              {
                "link": "https://www.cisa.gov/sites/default/files/publications/isc-planning-response-active-shooter-guide-non-fouo-nov-2015-508.pdf",
                "text": "(link)"
              }
            ]
          },
          {
            "items": [
              {
                "text": "American School Counselor Association, National Association of Elementary School Principals, National Association of School Psychologists, National Association of School Resource Officers, National Association of Secondary School Principals, School Social Work Association of America, A Framework for Safe and Successful Schools, 2013, updated March 2015"
              },
              {
                "link": "https://www.nasponline.org/resources-and-publications/resources/school-safety-and-crisis/a-framework-for-safe-and-successful-schools",
                "text": "(click here)"
              }
            ]
          }
        ]
      }
    ]
  },
  "eop": {
    "key": "eop",
    "prompt": "Does the school have a security and emergency operations plan(s)?",
    "options": [
      {
        "value": "Very_Low",
        "label": "The school does not have written security or emergency operations plans. All security and emergency response procedures are communicated verbally and may be performed on an ad hoc basis."
      },
      {
        "value": "Low",
        "label": "The school has some written documentation that addresses some safety and emergency operations policies, programs, and/or procedures (e.g., a checklist), but comprehensive plans do not exist."
      },
      {
        "value": "Medium",
        "label": "The school has a security and emergency operations plan(s). The security plan or portion of the plan addresses some, but not all, of the following areas: (1) Risk assessment; (2) Identification of critical assets; (3) Physical security and security force (if applicable); and (4) Communications. The emergency operations plan or portion of the plan addresses some, but not all, of the following functional areas: (1) Hazard analysis; (2) Communications; (3) Evacuation, shelter-in-place, and lockdown; (4) Specialized response actions and communication protocols necessary for areas with unique physical characteristics (e.g., cafeteria, auditorium, portable classrooms); (5) Accounting for all persons; (6) Reunification; (7) Continuity of operations; (8) Security; (9) Recovery; and (10) Health and medical. The plan(s) addresses maintaining point-of-contact lists, training and exercises, and plan maintenance (e.g., review and revision)."
      },
      {
        "value": "High",
        "label": "The school has a comprehensive security and emergency operations plan(s) that addresses all of the areas listed previously. However, senior management has not approved the plan(s) or coordinated the plan(s) with community partners (e.g., first responders, local emergency management staff)."
      },
      {
        "value": "Very_High",
        "label": "The school has a comprehensive security and emergency operations plan(s) that addresses all of the areas listed previously. Senior management has approved the plan(s) and has coordinated the plan(s) with community partners (e.g., first responders, local emergency management staff)."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Establish a collaborative planning team with representatives from a wide range of school personnel to develop security and emergency operations plans.",
      "Low": "Review, validate, and expand on the existing plan(s). Establish a regular cycle of training, exercising, evaluating, reviewing, and updating the plan(s).",
      "Medium": "Update the plan(s) to address all of the areas listed above.",
      "High": "Submit the plan(s) for senior management review. Coordinate the plan(s) with community partners.",
      "Very_High": "Regularly train, exercise, evaluate, review, and update the plan(s). Effective plans are never finished and should be updated at least every 2 years based on exercise results, research, and evolving threats."
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "items": [
              {
                "text": "A security plan provides direction on the school’s security management and policies. It is a critical component of an effective security program. An emergency operations plan (also known as an emergency plan, emergency action plan, emergency response plan, and crisis plan) addresses threats and hazards to the school and addresses safety needs before, during, and after an incident, which can be aligned with the national approach to preparedness efforts. Presidential Policy Directive (PPD)-8 defines preparedness around five mission areas: Prevention, Protection, Mitigation, Response, and Recovery. The majority of Prevention, Protection, and Mitigation activities generally occur before an incident, although these three mission areas have ongoing activities that can occur throughout an incident. Response activities occur during an incident, and Recovery activities can begin during an incident and occur after an incident. For more information about PPD-8, visit the Federal Emergency Management Agency (FEMA) Learn About Presidential Policy Directive-8 webpage. Some schools have unique situations such as open areas without lockable doors, portable buildings, and other security challenges. Plans should specifically address these unique facilities and areas."
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "separator": " ",
            "items": [
              {
                "text": "(1)"
              },
              {
                "text": "ISC, Facility Security Plan: An Interagency Security Committee Guide, 1st Edition, February 2015, accessed April 24, 2018"
              },
              {
                "link": "https://www.cisa.gov/sites/default/files/publications/ISC-Facility-Security-Plan-Guide-2015-508.pdf"
              }
            ]
          },
          {
            "separator": " ",
            "items": [
              {
                "text": "(2)"
              },
              {
                "text": "ED, HHS, DHS, and DOJ, Guide for Developing High-Quality School Emergency Operations Plans, 2013, accessed April 24, 2018"
              },
              {
                "link": "https://rems.ed.gov/docs/REMS_K-12_Guide_508.pdf"
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "“EOP Interactive Tools,” REMS TA Center "
              },
              {
                "link": "https://rems.ed.gov/EOPinteractivetools.aspx"
              }
            ]
          },
          {
            "items": [
              {
                "text": "Hamed Negron-Perez, “Developing High-Quality School Emergency Operations Plans: An Overview,” REMS TA Center, August 7, 2013 "
              },
              {
                "link": "https://rems.ed.gov/DevelopingHigh-QualitySchool.aspx"
              }
            ]
          },
          {
            "items": [
              {
                "text": "Amy Banks, et al., “Using Federal Resources to Enhance Campus Security and Emergency Management Planning,” REMS TA Center, November 25, 2013 "
              },
              {
                "link": "https://rems.ed.gov/EnhanceCampusSecurityandEMP.aspx"
              }
            ]
          },
          {
            "items": [
              {
                "text": "Jim Lee, “Developing High-Quality School Emergency Operations Plans: A Collaborative Process,” REMS TA Center, October 15, 2014 "
              },
              {
                "link": "https://rems.ed.gov/DevelopingHQSchoolEOPs.aspx"
              }
            ]
          },
          {
            "items": [
              {
                "text": "Minnesota Department of Public Safety, Division of Homeland Security and Emergency Management, Minnesota School Safety Center Program, Comprehensive School Safety Guide, 2011 ("
              },
              {
                "link": "https://dps.mn.gov/divisions/hsem/mn-school-safety-center/Documents/comprehensive-school-safety-guide.pdf",
                "text": "link"
              },
              {
                "text": ")"
              }
            ]
          },
          {
            "items": [
              {
                "text": "FEMA, Sample School Emergency Operations Plan, November 2013 "
              },
              {
                "link": "https://training.fema.gov/programs/emischool/el361toolkit/assets/sampleplan.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "eop_faculty_training": {
    "key": "eop_faculty_training",
    "prompt": "Are faculty and staff trained on the security and emergency operations plan(s)?",
    "options": [
      {
        "value": "Very_Low",
        "label": "The school does not train faculty and staff on the plan(s)."
      },
      {
        "value": "Low",
        "label": "The school provides some information or training to some, but not all, faculty and staff at initial employment. The school may provide refresher training at the beginning of the school year but no other follow-up throughout the year."
      },
      {
        "value": "Medium",
        "label": "The school provides training to some, but not all, faculty and staff at initial employment, as well as refresher training at the beginning of each school year and recurring training throughout the year. Although faculty and staff are trained on the appropriate emergency actions for their primary locations (e.g., classrooms), they may not be aware of what to do if an incident occurs when they are somewhere else in the school building (e.g., gymnasium, hallways)."
      },
      {
        "value": "High",
        "label": "The school provides comprehensive training to all faculty and staff at initial employment. Faculty and staff are trained on appropriate emergency actions regardless of their location in the school building. The training may include refresher training at the beginning of the school year but no other follow-up throughout the year."
      },
      {
        "value": "Very_High",
        "label": "The school provides comprehensive training to all faculty and staff at initial employment, as well as refresher training at the beginning of each school year and recurring training throughout the year. Faculty and staff are trained on appropriate emergency actions regardless of their location in the school building."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Provide training to critical faculty and staff at least at initial employment and at the beginning of the school year.",
      "Low": "Provide follow-up training to critical faculty and staff throughout the year.",
      "Medium": "Expand training to include all faculty and staff. Train faculty and staff on appropriate emergency actions regardless of their location in the school building.",
      "High": "Provide follow-up training to faculty and staff throughout the year.",
      "Very_High": "Regularly assess the security and emergency plan(s) training program to identify and remedy any gaps or shortfalls. Ensure the completion of all training is accurately documented."
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "items": [
              {
                "text": "Training may include presentations developed within the school or school district. Often, guest speakers or presentations from security subject matter experts are more beneficial. Providing checklists, flip cards, or other routinely updated and refreshed reference material may also aid training."
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "separator": " ",
            "items": [
              {
                "text": "(1)"
              },
              {
                "text": "ISC, Facility Security Plan: An Interagency Security Committee Guide, 1st Edition, February 2015, accessed April 24, 2018"
              },
              {
                "link": "https://www.cisa.gov/sites/default/files/publications/ISC-Facility-Security-Plan-Guide-2015-508.pdf"
              }
            ]
          },
          {
            "separator": " ",
            "items": [
              {
                "text": "(2)"
              },
              {
                "text": "ED, HHS, DHS, and DOJ, Guide for Developing High-Quality School Emergency Operations Plans, 2013, accessed April 24, 2018"
              },
              {
                "link": "https://rems.ed.gov/docs/REMS_K-12_Guide_508.pdf"
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "“Training,” DHS "
              },
              {
                "link": "https://www.ready.gov/business/implementation/training"
              }
            ]
          },
          {
            "items": [
              {
                "text": "National Incident Management System,” FEMA, August 10, 2015 "
              },
              {
                "link": "https://training.fema.gov/nims/"
              }
            ]
          },
          {
            "items": [
              {
                "text": "Virginia Department of Education, Resource Guide: Crisis Management and Emergency Response in Virginia Schools, November 29, 2007 "
              },
              {
                "link": "http://www.doe.virginia.gov/support/safety_crisis_management/school_safety/emergency_crisis_management/crisis_mgmt_emer-response_guide.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "eop_student_training": {
    "key": "eop_student_training",
    "prompt": "Are students trained on elements of the security and emergency operations plan(s)?",
    "options": [
      {
        "value": "Very_Low",
        "label": "The school does not train students on elements of the plan(s) (e.g., evacuation routes)."
      },
      {
        "value": "Medium",
        "label": "The school provides some training to students at the beginning of each school year. Although students are trained on the appropriate emergency actions for their primary locations (e.g., classrooms), they may not be aware of what to do if an incident occurs when they are somewhere else in the school building (e.g., gymnasium, hallways)."
      },
      {
        "value": "Very_High",
        "label": "The school provides comprehensive training to students at the beginning of each school year and recurring training throughout the year. Students are trained on appropriate emergency actions regardless of their location in the school building."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Provide training to critical faculty and staff at least at initial employment and at the beginning of the school year.",
      "Medium": "Evaluate the need to provide more frequent training to students. Evaluate the completeness of training and provide additional information as necessary. Train students on appropriate emergency actions regardless of their location in the school building.",
      "Very_High": "Regularly assess the training program to identify and remedy any gaps or shortfalls. Ensure the completion of all training is accurately documented."
    },
    "moreInfo": [
      {
        "title": "References",
        "body": [
          {
            "separator": " ",
            "items": [
              {
                "text": "ED, HHS, DHS, and DOJ, Guide for Developing High-Quality School Emergency Operations Plans, 2013, accessed April 24, 2018 "
              },
              {
                "link": "https://rems.ed.gov/docs/REMS_K-12_Guide_508.pdf"
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "Virginia Department of Education, Resource Guide: Crisis Management and Emergency Response in Virginia Schools, November 29, 2007."
              },
              {
                "link": "http://www.doe.virginia.gov/support/safety_crisis_management/school_safety/emergency_crisis_management/crisis_mgmt_emer-response_guide.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "eop_security_exercise": {
    "key": "eop_security_exercise",
    "prompt": "Does the school exercise the security and emergency operations plan(s)?",
    "options": [
      {
        "value": "Very_Low",
        "label": "The school does not exercise the plan(s) on an annual basis. The school may conduct fire, tornado, or earthquake drills."
      },
      {
        "value": "Low",
        "label": "The school exercises the plan(s) at least once annually. Exercises do not include community partners (e.g., first responders, local emergency management staff). The school may not develop an after-action report. The school may conduct fire, tornado, or earthquake drills."
      },
      {
        "value": "Medium",
        "label": "The school exercises the plan(s) at least once annually. Exercises are tabletop or drills. Exercises include community partners (e.g., first responders, local emergency management staff). The school develops an after-action report and uses it to improve plans and procedures. The school may conduct fire, tornado, or earthquake drills."
      },
      {
        "value": "High",
        "label": "The school exercises the plan(s) at least once annually. Exercises are functional. Exercises include community partners (e.g., first responders, local emergency management staff). The school develops an after-action report and uses it to improve plans and procedures. The school may conduct fire, tornado, or earthquake drills."
      },
      {
        "value": "Very_High",
        "label": "The school exercises the plan(s) at least once annually. Exercises are simultaneous functional exercises. Exercises include community partners (e.g., first responders, local emergency management staff). The school develops an after-action report and uses it to improve plans and procedures. The school may conduct fire, tornado, or earthquake drills. "
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Exercise the plan at least once annually.",
      "Low": "Invite community partners to participate in exercises that test the plan. Document the results of all exercises, areas for improvement, and lessons learned in after-action reports.",
      "Medium": "Conduct a functional exercise where events are projected through a scenario with updates that drive activity. A functional exercise is conducted in a realistic, real-time environment; however, movement of personnel and equipment is usually simulated.",
      "High": "Conduct simultaneous functional exercises to validate and evaluate multiple capabilities and functions.",
      "Very_High": "After conducting multiple functional exercises, explore the option of holding a full-scale exercise as a capstone that involves multiple agencies, organizations, and jurisdictions. Full-scale exercises are the most complex and resource-intensive type of exercise. They often include many players operating under a cooperative system such as the Incident Command System. Consider involving students and parents in full-scale exercises."
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "items": [
              {
                "text": "An exercise is a way to train for, assess, practice, and improve performance in a risk-free environment. Exercises can be used for testing and validating policies, plans, procedures, training, equipment, and agreements; clarifying and training personnel in roles and responsibilities; improving coordination and communications; and identifying gaps and opportunities for improvement."
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "separator": " ",
            "items": [
              {
                "text": "DHS, Homeland Security Exercise and Evaluation Program (HSEEP), April 2013, accessed April 25, 2018  "
              },
              {
                "link": "https://preptoolkit.fema.gov/documents/1269813/1269861/HSEEP_Revision_Apr13_Final.pdf/65bc7843-1d10-47b7-bc0d-45118a4d21da"
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "Exercises,” DHS"
              },
              {
                "link": "http://www.doe.virginia.gov/support/safety_crisis_management/school_safety/emergency_crisis_management/crisis_mgmt_emer-response_guide.pdf"
              }
            ]
          },
          {
            "items": [
              {
                "text": "“HSEEP Policy and Guidance,” FEMA Preparedness Toolkit "
              },
              {
                "link": "https://preptoolkit.fema.gov/web/hseep-resources"
              }
            ]
          },
          {
            "items": [
              {
                "text": "“National Incident Management System,” FEMA, August 10, 2015 "
              },
              {
                "link": "https://training.fema.gov/nims/"
              }
            ]
          },
          {
            "items": [
              {
                "text": "FEMA, “Multihazard Emergency Planning for Schools Site Index,” "
              },
              {
                "link": "https://training.fema.gov/programs/emischool/el361toolkit/siteindex.htm"
              }
            ]
          },
          {
            "items": [
              {
                "text": "Virginia Department of Education, Resource Guide: Crisis Management and Emergency Response in Virginia Schools, November 29, 2007, "
              },
              {
                "link": "http://www.doe.virginia.gov/support/safety_crisis_management/school_safety/emergency_crisis_management/crisis_mgmt_emer-response_guide.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "mass_notifications": {
    "key": "mass_notifications",
    "prompt": "Does the school have mass notification capabilities?",
    "options": [
      {
        "value": "Very_Low",
        "label": "The school does not have a way to alert the entire school of impending danger."
      },
      {
        "value": "Low",
        "label": "The school has a public address system to communicate audible warnings onsite. However, the school does not have a system for sharing critical information in a timely manner with members of the school community (e.g., parents)."
      },
      {
        "value": "Medium",
        "label": "The school has a public address system for onsite emergency communications but lacks ways to communicate warnings effectively to staff and students with sensory or cognitive disabilities. The school has an emergency mass communication system for the offsite school community, but it does not periodically test the system."
      },
      {
        "value": "High",
        "label": "The school has both audio and visual mass notification systems for onsite emergency communications, as well as a dedicated emergency mass communication system for the offsite school community. However, not all classrooms have capabilities for two-way verbal communication with the school’s administrative or security offices."
      },
      {
        "value": "Very_High",
        "label": "The school has both audio and visual mass notification systems for onsite emergency communications inside and outside the building. They are unique and separate from communications channels used for routine announcements. A dedicated emergency mass communication system for the offsite school community exists. Every classroom has two-way classroom communication capabilities."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Install a public address system to provide a means of mass communication and to provide warning and alert information, along with actions to take before and after an incident.",
      "Low": "Implement an emergency mass communication system to share critical information with the school's offsite community.",
      "Medium": "Explore options to communicate warnings more effectively beyond only audible alerts, such as strobe lights. Test the emergency mass communication system periodically so users are familiar with its use.",
      "High": "Provide two-way classroom communication capabilities, which may consist of intercoms, phones, likert-radios, or other systems. Two-way classroom communication capabilities can provide a way to alert school administrators that a serious incident is taking place.",
      "Very_High": "Test mass notification systems. Ensure faculty, staff, and students know how to respond appropriately to alerts onsite. Ensure emergency mass notification systems for the offsite school community are working effectively."
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "items": [
              {
                "text": "The mass notification systems available today offer capabilities far beyond those of traditional public address system. The goal of these systems is to broadcast information rapidly to facilitate appropriate response and action to students, faculty, staff, visitors, first responders, families, and the public. New technologies have created the ability to reach people using multiple methods such as public announcement, email, text message, desktop notification, etc."
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "separator": " ",
            "items": [
              {
                "text": "DHS, Buildings and Infrastructure Protection Series: Primer to Design Safe School Projects in Case of Terrorist Attacks and School Shootings (FEMA-428/BIPS-07), January 2012, accessed May 4, 2018,   "
              },
              {
                "link": "https://www.dhs.gov/xlibrary/assets/st/bips07_428_schools.pdf"
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "Richard Hogue, Unarmed Respond to School Active Shooter Events, January 3, 2018, "
              },
              {
                "link": "https://www.in.gov/isp/files/ISP_School_Unarmed_Response_to_Active_Shooter_Event_Notes.pdf"
              }
            ]
          },
          {
            "items": [
              {
                "text": "Minnesota Department of Public Safety, Division of Homeland Security and Emergency Management, Minnesota School Safety Center Program, Comprehensive School Safety Guide, 2011, "
              },
              {
                "link": "https://dps.mn.gov/divisions/hsem/mn-school-safety-center/Documents/comprehensive-school-safety-guide.pdf"
              }
            ]
          },
          {
            "items": [
              {
                "text": "Lawrence J. Fennelly and Marianna A. Perry, The Handbook for School Safety and Security: Best Practices and Procedures (Amsterdam, The Netherlands: Elsevier 2014). "
              }
            ]
          }
        ]
      }
    ]
  },
  "coordinate_first_responders": {
    "key": "coordinate_first_responders",
    "prompt": "Does the school coordinate with first responders for enhanced emergency preparedness?",
    "options": [
      {
        "value": "Very_Low",
        "label": "The school has not coordinated with first responders regarding emergency preparedness."
      },
      {
        "value": "Low",
        "label": "The school has provided the security and emergency operations plan(s) to first responders, but has not solicited or implemented their input on the plan. The school has not shared critical information about the building or facilities (e.g., floor plans, location of critical assets or areas, and notification and contact lists)."
      },
      {
        "value": "Medium",
        "label": "The school has coordinated the security and emergency operations plan(s) with first responders and has provided critical building and facilities information. The school has not created crisis response kits for first responders to use in an emergency."
      },
      {
        "value": "High",
        "label": "The school has coordinated the security and emergency operations plan(s) with first responders; has provided critical building and facilities information; and has a crisis response kit for first responders. However, the school has not coordinated with first responders regarding the contents of the kit. The school may keep the kit in an unsecured location. The school has not designated an emergency response team to meet first responders as they arrive onsite in a major critical incident to provide them with the crisis response kit. The school has not implemented a facility numbering system."
      },
      {
        "value": "Very_High",
        "label": "The school has coordinated the security and emergency operations plan(s) with first responders; has provided critical building and facilities information; has a crisis response kit for first responders; and has implemented a facility numbering system that was developed in coordination with first responders. The contents of the crisis response kit were developed in coordination with local law enforcement and fire response agencies. The school keeps the kit in a secure but easily accessible location. The school has designated an emergency response team to meet and assist first responders as they arrive onsite in a major critical incident. The school also has interoperable likert-radios with law enforcement."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Provide the school's security and emergency operations plan(s) to local law enforcement and other first responders as necessary.",
      "Low": "Review the school's security and emergency operations plan(s) with first responders, and make changes as necessary. Share critical building and facilities information with first responders.",
      "Medium": "Create a crisis response kit to provide first responders and key school administrators that contains the information and equipment needed for effective management of a major critical incident. At a minimum, the kit should include the following information: (1) Camera locations; (2) Accurate floor plans; (3) List of key personnel and phone numbers (senior staff, facilities, security, etc.); (4) Master key(s), elevator key(s), and master access card(s); (5) Building/security force likert-radio (if applicable); and (6) Evacuation routes and assembly area locations.",
      "High": "Collaborate with first responders (including law enforcement, fire, and emergency medical services) regarding the specific information to include in the school's crisis response kit. Place the crisis kit in a secure area where school administrators and first responders can easily access it. Due to the sensitive nature of the contents of the kit, take measures to safeguard it appropriately. Establish an emergency team prepared to provide first responders with the crisis response kit and access to utilities, keys, broadcast systems, building schematics, etc. Designate more than one individual to be responsible for meeting and assisting first responders. Develop a facility numbering system in coordination with first responders.",
      "Very_High": "Continue coordination. Invite law enforcement to conduct training and exercises at the school. Review the contents of the crisis response kit quarterly to ensure it remains up to date. Use the crisis kit during a drill. Test the likert-radio(s) on a monthly basis, and keep batteries charged."
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "items": [
              {
                "text": "(1) A crisis response kit provides easy access to relevant information for first responders to enhance emergency response. Crisis kit contents usually include floor plans, point-of-contact lists, evacuation routes, and assembly area locations, among other components. (2) Facility numbering systems clarify locations communicated by first responders during incident response and by individuals relaying information to first responders. One example would be to number exterior doors above doorways in a clockwise scheme, beginning with 1 at the main entrance and using identical numbering inside the building. "
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "separator": " ",
            "items": [
              {
                "text": "(1)"
              },
              {
                "text": "ISC, Planning and Response to an Active Shooter: An Interagency Security Committee Policy and Best Practices Guide, November 2015, accessed May 8, 2018, "
              },
              {
                "link": "https://www.cisa.gov/sites/default/files/publications/ISC-Facility-Security-Plan-Guide-2015-508.pdf"
              }
            ]
          },
          {
            "separator": " ",
            "items": [
              {
                "text": "(2)"
              },
              {
                "text": "Indiana Department of Homeland Security, Indiana School Safety Guidelines for Emergency Response Systems, 2017, accessed June 11, 2018 "
              },
              {
                "link": "https://www.in.gov/dhs/files/Indiana-School-Safety-Guidelines.pdf"
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "California Attorney General, Crime and Violence Prevention Center and California Department of Education, Safe Schools and Violence Prevention Office, Crisis Response Box, undated, "
              },
              {
                "link": "https://www.cde.ca.gov/ls/ss/cp/documents/crisisrespbox.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "security_information_staff": {
    "key": "security_information_staff",
    "prompt": "Does the school provide security information to faculty and staff?",
    "options": [
      {
        "value": "Very_Low",
        "label": "The school does not provide security information to faculty and staff."
      },
      {
        "value": "Low",
        "label": "The school provides information about specific security incidents to faculty and staff. The school does not provide security awareness information."
      },
      {
        "value": "Medium",
        "label": "The school provides information about specific security incidents as well as security awareness information to faculty and staff on a recurring basis. However, security awareness information is limited in scope and availability (e.g., provided occasionally at meetings)."
      },
      {
        "value": "High",
        "label": "The school provides information about specific security incidents as well as security awareness information to faculty, staff, and students on a recurring basis. Security awareness information is detailed and comprehensive but limited in availability (e.g., emails only). "
      },
      {
        "value": "Very_High",
        "label": "The school provides information about specific security incidents as well as security awareness information to faculty, staff, and students on a recurring basis. Security awareness information is comprehensive and communicated through a variety of formats (e.g., emails, posters, announcements, regular meetings)."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Provide information about specific security incidents to faculty and staff.",
      "Low": "Routinely provide security awareness information in addition to information about specific security incidents.",
      "Medium": "Provide more detailed and comprehensive security awareness information. Disseminate information through regular meetings.",
      "High": "Disseminate security awareness information through a variety of formats (e.g., emails, posters, announcements, regular meetings).",
      "Very_High": "Continue to provide security information to faculty and staff. Solicit feedback regarding the usefulness of the type of information provided."
    },
    "moreInfo": [
      {
        "title": "References",
        "body": [
          {
            "separator": " ",
            "items": [
              {
                "text": "ASIS International, Protection of Assets: Crisis Management (Alexandria, VA: ASIS International, 2011). "
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "Sarah Goodrum and William Woodward, Report on the Arapahoe High School Shooting: Lessons Learned on Information Sharing, Threat Assessment, and Systems Integrity, January 18, 2016, "
              },
              {
                "link": "http://cdpsdocs.state.co.us/safeschools/Resources/AHS-reports/CSPV.AHSFullReport.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "suspicious_packages": {
    "key": "suspicious_packages",
    "prompt": "Does the school have procedures for suspicious packages (including mail)?",
    "options": [
      {
        "value": "Very_Low",
        "label": "The school does not have procedures for suspicious packages."
      },
      {
        "value": "Low",
        "label": "The school has some written documentation that addresses suspicious packages, but detailed, comprehensive procedures do not exist."
      },
      {
        "value": "Medium",
        "label": "The school has suspicious package procedures, but some information may be lacking. The school may not effectively communicate these procedures to faculty or staff. Students are aware that if they encounter a suspicious package, they should leave it alone and notify faculty or staff."
      },
      {
        "value": "High",
        "label": "The school has detailed, comprehensive suspicious package procedures that define characteristics and outline appropriate response actions. The school effectively communicates these procedures to faculty and staff but does not provide any training. Students are aware that if they encounter a suspicious package, they should leave it alone and notify faculty or staff."
      },
      {
        "value": "Very_High",
        "label": "Detailed, comprehensive procedures define common characteristics of suspicious packages and outline appropriate response actions. The school communicates these procedures to faculty and staff, and provides training as appropriate. Students are aware that if they encounter a suspicious package, they should leave it alone and notify faculty or staff."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Develop a checklist to help personnel identify suspicious packages.",
      "Low": "Develop detailed and comprehensive procedures for suspicious packages that define their characteristics and outline appropriate response actions. Share procedures with faculty and staff. Ensure students are aware that if they encounter a suspicious package, they should leave it alone and notify faculty or staff.",
      "Medium": "Review the existing suspicious package procedures, and incorporate additional information as needed. Share the updated procedures with faculty and staff.",
      "High": "Provide regular training on suspicious package procedures as appropriate.",
      "Very_High": "Test suspicious package procedures regularly. Review suspicious package procedures to identify and remedy any gaps or shortfalls."
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "separator": " ",
            "items": [
              {
                "text": "A suspicious package may contain harmful and dangerous materials. Suspicious packages vary in size and shape, and can be boxes, envelopes, backpacks, bags, or other items, but are usually left unattended and seem out of place. They may have powdery substances felt through or appearing on them; oily stains or discolorations on the exterior; strange odors; and/or excessive packaging material, like tape or string. The package may be lopsided or bulky; it may emit ticking sounds, have protruding wires, or exposed aluminum foil. Active shooters may use explosives or suspicious packages as a diversion or distraction. "
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "separator": " ",
            "items": [
              {
                "text": "DHS and ISC, Best Practices for Mail Screening and Handling Processes: A Guide for the Public and Private Sectors, 1st Edition, September 27, 2012, accessed April 26, 2018, "
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "”Nationwide Suspicious Activity Reporting (SAR) Initiative (NSI),”  "
              },
              {
                "link": "https://nsi.ncirc.gov/"
              }
            ]
          },
          {
            "items": [
              {
                "text": "What To Do – Bomb Threat,” DHS, March 9, 2018,  "
              },
              {
                "link": "https://www.dhs.gov/what-to-do-bomb-threat"
              }
            ]
          },
          {
            "items": [
              {
                "text": "AWR-335 — Response to Suspicious Behaviors and Items for Bombing Prevention (RSBI),” Virtual Instructor Led Training (VILT) Awareness Courses, DHS Office for Bombing Prevention "
              },
              {
                "link": "https://cdp.dhs.gov/obp"
              }
            ]
          },
          {
            "items": [
              {
                "text": "“Bomb Threats,” Colorado Department of Public Safety, School Safety Resource Center,  "
              },
              {
                "link": "https://cssrc.colorado.gov/bomb-threats"
              }
            ]
          }
        ]
      }
    ]
  },
  "searches": {
    "key": "searches",
    "prompt": "Does the school have policies for conducting searches for weapons, drugs, and other contraband?",
    "options": [
      {
        "value": "Very_Low",
        "label": "The school lacks search policies."
      },
      {
        "value": "Medium",
        "label": "The school has some written documentation for conducting searches, but detailed, comprehensive policies and procedures do not exist."
      },
      {
        "value": "Very_High",
        "label": "The school has detailed, comprehensive policies for conducting searches. Security personnel have access to this information and receive training."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Develop policies and procedures for conducting searches, such as random locker searches.",
      "Medium": "Develop detailed and comprehensive search policies and procedures. Share these with security personnel, and provide training.",
      "Very_High": "Collaborate with law enforcement to review search policies and procedures to identify and remedy any gaps or shortfalls"
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "separator": " ",
            "items": [
              {
                "text": "School personnel are encouraged to seek legal advice before implementing any policy on the search or seizure of students’ property. While a warrant may not be required, school officials must have valid justification for any search or seizure. The sources below include additional information on this topic."
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "separator": " ",
            "items": [
              {
                "text": "DOJ Bureau of Justice Assistance and International Association of Chiefs of Police, Guide for Preventing and Responding to School Violence, 2nd Edition, 2012, accessed April 25, 2018 "
              },
              {
                "link": "https://bja.ojp.gov/sites/g/files/xyckuh186/files/Publications/IACP_School_Violence.pdf"
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "Kate R. Ehlenberger, “The Right to Search Students,” Understanding the Law, December 2001/January 2002, Vol. 59, No. 4  "
              },
              {
                "link": "http://www.ascd.org/publications/educational-leadership/dec01/vol59/num04/The-Right-to-Search-Students.aspx"
              }
            ]
          },
          {
            "items": [
              {
                "text": "“School Discipline Laws & Regulations by Category: In-School Discipline, Use of student and locker searches,” National Center on Safe Supportive Learning Environments "
              },
              {
                "link": "https://safesupportivelearning.ed.gov/discipline-compendium/choose-state?field_sub_category_value=Use+of+student+and+locker+searches"
              }
            ]
          },
          {
            "items": [
              {
                "text": "Minnesota Department of Public Safety, Division of Homeland Security and Emergency Management, Minnesota School Safety Center Program, Comprehensive School Safety Guide, 2011 "
              },
              {
                "link": "https://dps.mn.gov/divisions/hsem/mn-school-safety-center/Documents/comprehensive-school-safety-guide.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "external_seurity_eop": {
    "key": "external_seurity_eop",
    "prompt": "Does the school participate in any external security or emergency preparedness working groups?",
    "options": [
      {
        "value": "Very_Low",
        "label": "The school does not participate in any federal, state, local, or private sector security or emergency preparedness working groups."
      },
      {
        "value": "Low",
        "label": "School personnel are connected with a federal, state, local, or private sector security or emergency preparedness working group. They may not regularly attend meetings, but they receive information."
      },
      {
        "value": "Medium",
        "label": "School personnel are connected with a security or emergency preparedness working group. They regularly attend meetings and receive information."
      },
      {
        "value": "High",
        "label": "School personnel are connected with a security or emergency preparedness working group. They regularly attend meetings and receive information. They may participate in special events or activities that the working group sponsors. They may have leadership positions in the group."
      },
      {
        "value": "Very_High",
        "label": "School personnel are connected with two or more security or emergency preparedness working groups. They regularly attend meetings and receive information. They may participate in special events or activities that a working group sponsors. They may have leadership positions in a group."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Engage with school district personnel to identify opportunities for federal, state, local, or private sector working group participation.",
      "Low": "Prioritize attendance at security or emergency preparedness working group meetings.",
      "Medium": "Seek out more ways for school personnel to benefit from participating in the security or emergency preparedness working group, for example, by getting involving in special events or activities or by taking leadership positions in the group",
      "High": "Join other security or emergency preparedness working groups to increase engagement and gain additional resources in these areas.",
      "Very_High": "Assess the need to engage with additional security or emergency preparedness working groups."
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "separator": " ",
            "items": [
              {
                "text": "A security or emergency preparedness working group (also called work groups, task forces, and a variety of other names) are where practitioners, often representing various disciplines, come together to contribute their time, expertise, passion, and experiences to help develop and implement strategies to improve overall school security and/or emergency preparedness. This type of working group would be external to the school or school district and may be at federal, state, or local level or part of the private sector. "
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "separator": " ",
            "items": [
              {
                "text": "DHS, NIPP 2013: Partnering for Critical Infrastructure Security and Resilience, accessed April 24, 2018 "
              },
              {
                "link": "https://www.cisa.gov/sites/default/files/publications/national-infrastructure-protection-plan-2013-508.pdf"
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "ED, REMS TA Center, Prevention for Schools and School Districts Fact Sheet, 2017  "
              },
              {
                "link": "https://rems.ed.gov/Docs/Prevention_Fact_Sheet_508C.pdf"
              }
            ]
          },
          {
            "items": [
              {
                "text": "ED, REMS TA Center, Protection for Schools and School Districts Fact Sheet, 2017,  "
              },
              {
                "link": "https://rems.ed.gov/Docs/Protection_Fact_Sheet_508C.pdf"
              }
            ]
          },
          {
            "items": [
              {
                "text": "ED, REMS TA Center, Mitigation for Schools and School Districts Fact Sheet, 2017, "
              },
              {
                "link": "https://rems.ed.gov/Docs/Mitigation_Fact_Sheet_508C.pdf"
              }
            ]
          },
          {
            "items": [
              {
                "text": "Short list of sample working groups (not specifically endorsed by DHS) "
              }
            ]
          },
          {
            "items": [
              {
                "text": "National Association of School Safety and Law Enforcement Officials, "
              },
              {
                "link": "http://www.nassleo.org/index.php"
              }
            ]
          },
          {
            "items": [
              {
                "text": "Educator’s School Safety Network,  "
              },
              {
                "link": "http://eschoolsafety.org"
              }
            ]
          },
          {
            "items": [
              {
                "text": "ASIS International, School Safety & Security Council, "
              },
              {
                "link": "https://www.asisonline.org/get-involved/councils"
              }
            ]
          }
        ]
      }
    ]
  },
  "security_force": {
    "key": "security_force",
    "prompt": "Does the school have a security force?",
    "options": [
      {
        "value": "Very_Low",
        "label": "The school does not have a security force."
      },
      {
        "value": "Low",
        "label": "The school does not have a defined security force, but faculty or staff may have an additional duty related to security."
      },
      {
        "value": "Medium",
        "label": "A security force exists within the district but is assigned additional duties. The security force may be a contracted guard, law enforcement, or an employee hired for the purpose of security. This force may occasionally visit the school."
      },
      {
        "value": "High",
        "label": "A security force is employed or contracted for the specific purpose of school security. May visit the school routinely or during events or investigations. This may include an SRO (law enforcement) who has other assigned duties or is responsible for multiple locations."
      },
      {
        "value": "Very_High",
        "label": "The school has an onsite security force (at school) at all times. The security force is employed or contracted for the specific purpose of school security. This may include an SRO (law enforcement) who is responsible for the single location and is assigned full-time."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Assign and provide training to a few faculty or staff to perform basic security tasks that include ensuring doors and windows are locked, coordinating active-shooter response procedures, addressing suspicious activity, and other security-related training.",
      "Low": "Explore the feasibility of establishing or contracting a security force at the district or school level to actively protect the school(s), property, and personnel, and to rapidly respond to security incidents and/or emergencies. Depending on state and local regulations, security force personnel may be subject to registration, licensing, certification, specific training, and other requirements.",
      "Medium": "Establish agreements or contracts to employ a security force with the specific purpose of school security. This force may visit the school routinely or during events or investigations. This may include an SRO (law enforcement) who has other assigned duties or is responsible for multiple locations.",
      "High": "Establish agreements or contracts to employ a security force with the specific purpose of being an onsite security force (at school) at all times. The security force is employed or contracted for the specific purpose of school security. This may include an SRO (law enforcement) who is responsible for the single location",
      "Very_High": "Routinely review the role of the security force and establish metrics for continuous improvement and modification to the role. Ensure the security force is actively engaged in the threat assessment team and assists or conducts ongoing training and development of faculty and staff as related to school security."
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "items": [
              {
                "text": "A security force is a group of school employees or contractors whose sole responsibilities are to provide security at a school. A security force does not include general school personnel who are trained in security awareness (i.e., observe and report) in addition to their regular duties."
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "items": [
              {
                "text": "Pennsylvania State Police, Domestic Security Division, Risk and Vulnerability Assessment Team, School Safety Report, 2012, accessed April 26, 2018"
              },
              {
                "link": "http://www.psp.pa.gov/public-safety/Documents/RVAT%20School%20Safety%20Report_FINAL%20v1.2.pdf"
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "DOJ Office of Community Oriented Policing Services, Beyond the Badge: Profile of a School Resource Officer - A Guide for Law Enforcement, October 2016"
              },
              {
                "link": "https://cops.usdoj.gov/RIC/Publications/cops-p358-pub.pdf"
              }
            ]
          },
          {
            "items": [
              {
                "text": "DOJ Office of Community Oriented Policing Services, Beyond the Badge: Profile of a School Resource Officer - A Guide for School Communities, October 2016"
              },
              {
                "link": "https://cops.usdoj.gov/RIC/Publications/cops-p357-pub.pdf"
              }
            ]
          },
          {
            "items": [
              {
                "text": "National Association of School Resource Officers, To Protect & Educate: The School Resource Officer and the Prevention of Violence in Schools, 2012"
              },
              {
                "link": "https://www.nasro.org/clientuploads/resources/NASRO-Protect-and-Educate.pdf"
              }
            ]
          },
          {
            "items": [
              {
                "text": "DHS, Buildings and Infrastructure Protection Series: Primer to Design Safe School Projects in Case of Terrorist Attacks and School Shootings (FEMA-428/BIPS-07), January 2012"
              },
              {
                "link": "https://www.dhs.gov/xlibrary/assets/st/bips07_428_schools.pdf"
              }
            ]
          },
          {
            "items": [
              {
                "text": "New Jersey School Security Task Force, New Jersey School Security Task Force Report and Recommendations, July 2015"
              },
              {
                "link": "https://www.state.nj.us/education/schools/security/TaskForceReport.pdf"
              }
            ]
          },
          {
            "items": [
              {
                "text": "Hanover Research, Best Practices in School Security, March 2013"
              },
              {
                "link": "https://littletonpublicschools.net/sites/default/files/mental%20health%20Best-Practices-in-School-Security.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "training_provided": {
    "key": "training_provided",
    "prompt": "What training related to active-shooter situations and general security issues does the school provide to the security force, staff, and faculty?",
    "options": [
      {
        "value": "Very_Low",
        "label": "The school has provided no training, no posters, and no awareness training for personnel within past 12 months."
      },
      {
        "value": "Low",
        "label": "The school has some posters or awareness training at initial employment for personnel. The training may include refresher training on emergency response activities at the beginning of school year, but no other follow-up takes place throughout the year. "
      },
      {
        "value": "Medium",
        "label": "Awareness training for faculty or staff occurs at initial employment and beginning of school year and recurring training one other time throughout the school year. If applicable, the SRO receives extensive, recurring training. If applicable, the security force receives training as stated in a contract or agreement. Posters are available in faculty areas. The school does not provide faculty and staff with training on how to help in a bleeding emergency before first responders arrive."
      },
      {
        "value": "High",
        "label": "The school provides awareness training and emergency response exercise at initial employment for faculty and staff. Routine training throughout the year is provided for personnel. Posters are available in faculty areas. Faculty and staff have been trained on bleeding control and have access to products to help stop traumatic hemorrhaging."
      },
      {
        "value": "Very_High",
        "label": "Personnel receive extensive, recurring training. If applicable, security force personnel provide routine monthly training to faculty and staff. Posters are available in faculty areas. All faculty and staff have been trained on bleeding control. Every classroom has a kit with products intended to stop traumatic hemorrhaging."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Use the DHS Active Shooter Preparedness website to establish initial training. At a minimum, provide awareness training at initial employment for all faculty and staff. Provide refresher or initial training at the beginning of the school year related to active shooter and other emergency response activities.",
      "Low": "Use the DHS Active Shooter Preparedness website as needed to enhance training. In addition to initial employment and at the beginning of the school year, conduct follow-up training at least one other time throughout the school year. Develop or contract a formal training program for those responsible for security such as emergency response Incident Command System/National Incident Management System. Provide continuation and in-service training for faculty and staff to maintain and enhance job proficiency. Document training and retain training records.",
      "Medium": "Use the DHS Active Shooter Preparedness website as needed to enhance training. Employ security professionals certified or licensed through a federal or state program. Ensure the training requirements they fulfilled to become certified or licensed will equip them to fulfill the school's security requirements. Establish a security topic as part of each faculty in-service event. Provide all staff awareness materials and training throughout the school year. Train faculty and staff how to provide immediate bleeding control in an emergency. Provide bleeding control supplies.",
      "High": "Use the DHS Active Shooter Preparedness website as needed to enhance training. Use security force, SRO, or law enforcement (local, state, or federal) to conduct additional training and exercises throughout the year. Provide all staff awareness materials and update materials throughout the school year.",
      "Very_High": "Use the DHS Active Shooter Preparedness website (or equivalent) to enhance training. Enhance training with realistic drills and exercises. Involve students in active-shooter drills or exercises to create a realistic simulation. Document lessons learned and implement updated actions."
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "items": [
              {
                "text": "No single response fits all active-shooter situations; however, making sure that each individual knows his or her emergency options and can react decisively will save valuable time. Depicting scenarios and considering emergency options in advance will assist individuals and groups in quickly selecting their best course of action. In general, three basic options are available in an active-shooter situation: run away from the shooter, hide in a secure place the shooter cannot access, or fight back to incapacitate the shooter. As a situation develops, faculty, staff, and students may need to use more than one option. During an active-shooter incident, faculty and staff should follow the plan and any instructions provided as the situation unfolds, but often they will have to rely on their own judgment to decide the best option."
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "items": [
              {
                "text": "ED, HHS, DHS, and DOJ, Guide for Developing High-Quality School Emergency Operations Plans, 2013, accessed April 24, 2018"
              },
              {
                "link": "https://rems.ed.gov/docs/REMS_K-12_Guide_508.pdf"
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "“Active Shooter Preparedness,” DHS"
              },
              {
                "link": "https://www.cisa.gov/active-shooter-preparedness"
              }
            ]
          },
          {
            "items": [
              {
                "text": "“Stop the Bleed,” DHS"
              },
              {
                "link": "https://www.dhs.gov/stopthebleed"
              }
            ]
          },
          {
            "items": [
              {
                "text": "National Incident Management System,” FEMA, August 10, 2015"
              },
              {
                "link": "https://training.fema.gov/nims/"
              }
            ]
          },
          {
            "items": [
              {
                "text": "National Association of School Psychologists and the National Association of School Resource Officers, Best Practice Considerations for Schools in Active Shooter and Other Armed Assailant Drills, December 10, 2014"
              },
              {
                "link": "https://www.nasponline.org/Documents/Research%20and%20Policy/Advocacy%20Resources/BP_Armed_Assailant_Drills.pdf"
              }
            ]
          },
          {
            "items": [
              {
                "text": "“Soft Target, Active Shooter, & School Security Resources,” ASIS"
              },
              {
                "link": "https://www.asisonline.org/publications--resources/security-topics/active-shooter/"
              }
            ]
          }
        ]
      }
    ]
  },
  "security-inspections": {
    "key": "security-inspections",
    "prompt": "Does the security force, staff, or faculty conduct security-related inspections of the school?",
    "options": [
      {
        "value": "Very_Low",
        "label": "No areas of the school are patrolled or inspected. Janitorial service personnel clean areas and report unusual items such as drugs or weapons. Emergency actions are improvised as reports are received."
      },
      {
        "value": "Low",
        "label": "A security force, staff, or faculty member occasionally inspects the school for unlocked doors, open windows, and/or suspicious packages, but they do not conduct a routine search or inspection, and have not received training. "
      },
      {
        "value": "Medium",
        "label": "A trained security force, staff, or faculty member occasionally inspects the school according to an established security checklist. K9 teams do not conduct patrols."
      },
      {
        "value": "High",
        "label": "A trained security force, staff, or faculty member routinely (i.e., daily) inspects most sensitive areas of the school including occasional sweeps of lockers, library, stage, and bleacher areas. K9 teams conduct ad hoc patrols to detect explosive materials such as firearm ammunition."
      },
      {
        "value": "Very_High",
        "label": "A trained security force, staff, or faculty member patrols and inspects all sensitive areas of school at least daily; they may patrol some areas more often. K9 teams conduct regular patrols to detect explosive materials such as firearm ammunition."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Establish procedures for reporting unusual items and suspicious activity to include notification to law enforcement. Assign a security force, staff, or faculty member to conduct security-related inspections of the school.",
      "Low": "Establish a security checklist that incorporates roles and responsibilities and what areas to evaluate. Common areas of interest include lockers, under stages in theaters or gymnasiums, bleacher areas, labs, shops, rooms or areas with tile ceilings, and similar areas that may have hazardous materials or places to easily conceal weapons, drugs, or other contraband. Be alert for graffiti or suspicious packages. Notify law enforcement of any activity. Provide training for personnel conducting inspections.",
      "Medium": "Conduct daily inspection of sensitive areas. Conduct occasional locker sweeps. Check the library and between rows of books. Ensure doors are not propped open, and windows, loading docks, and exterior doors are secured. Report suspicious activity, weapons, and drugs to the security force and law enforcement as needed. Explore the option of conducting K9 patrols to detect explosive materials such as firearm ammunition. This will likely require coordination with local law enforcement.",
      "High": "Continue daily inspections. Eliminate lockers where possible, and conduct daily inspections or establish lock check out and check-in process or something similar. Ensure a security force presence in a random pattern throughout the school focusing on sensitive areas. Explore the option of implementing regular K9 patrols.",
      "Very_High": "Train faculty and staff on identifying suspicious activity and search techniques. Augment existing security plans by including written inspection procedures. Train all staff and faculty on that section of the plan and conduct periodic drills and exercises to validate the plan."
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "items": [
              {
                "text": "K9 or K-9 is another name for what is more commonly known as a highly trained, tactical security dog or police dog. Random K9 patrols of parking lots and other areas when students are not present may uncover staged weapons or explosives. School administrators may also consider employing K9s at choke points (for example, school entrances at the beginning of the school day)."
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "items": [
              {
                "text": "DHS, Buildings and Infrastructure Protection Series: Primer to Design Safe School Projects in Case of Terrorist Attacks and School Shootings (FEMA-428/BIPS-07), January 2012, accessed April 24, 2018"
              },
              {
                "link": "https://www.dhs.gov/xlibrary/assets/st/bips07_428_schools.pdf"
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "Virginia Department of Criminal Justice Services and Virginia Department of Education, 2014 School Safety Inspection Checklist for Virginia Public Schools, 2014"
              },
              {
                "link": "https://www.dcjs.virginia.gov/sites/dcjs.virginia.gov/files/law-enforcement/files/vcscs/guidance-school-safety-inspection-checklist.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "security_force_armed": {
    "key": "security_force_armed",
    "prompt": "Are any members of the security force armed with lethal or non-lethal weapons?",
    "options": [
      {
        "value": "Very_Low",
        "label": "The school has no armed personnel (i.e., possessing a weapon such as handgun, rifle, shotgun) onsite, and law enforcement response is the only armed response."
      },
      {
        "value": "Medium",
        "label": "The security force is armed and receives recurring training. Alternatively, the security force may have non-lethal weapons and routine training."
      },
      {
        "value": "Very_High",
        "label": "The security force is armed (i.e., possesses a weapon such as handgun, rifle, shotgun) and has restraints. The security force may have less-than-lethal weapons. The school has established a formalized, trained threat response team."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Security organizations such as ASIS International note that potential assailants may be dissuaded from attacking a target if they know they will face armed resistance; however, the quality and quantity of defensive weapons must be an obstacle to the potential assailants. Given this, school officials may wish to consider arming their security force as part of their broader security strategy. Consider training, licensing, and arming personnel with less-than-lethal or lethal weapons to enable them to protect themselves and others. Considerations should include not only the threat, but also law enforcement response times; laws governing licensing armed security guards; laws concerning the use of force; training and certification requirements; workplace safety; liability; cost; and how students, parents, faculty, staff, and the public will perceive the presence of armed personnel within the school.",
      "Medium": "Consider training, licensing, and arming any additional or non-law enforcement security force with less-than-lethal or lethal weapons to enable them to protect themselves and others. Considerations should include not only the threat, but also law enforcement response times; laws governing licensing armed security guards; laws concerning the use of force; training and certification requirements; workplace safety; liability; cost; and how students, parents, faculty, staff, and the public will perceive the presence of armed personnel within the school.",
      "Very_High": "Continuously review the training and certification requirements; workplace safety; liability; cost; and how students, parents, faculty, staff, and the public perceive the presence of armed personnel within the school. Establish metrics to determine if the deterrent and protection is commensurate with the cost, liability, and public perception."
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "items": [
              {
                "text": "Security organizations such as ASIS International note that potential assailants may be dissuaded from attacking a target if they know they will face armed resistance; however, the quality and quantity of defensive weapons must be an obstacle to the potential assailants. Given this, school officials may wish to consider arming their security force as part of their broader security strategy."
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "separator": " ",
            "items": [
              {
                "text": "(1)"
              },
              {
                "text": "Thomas L. Norman, Risk Analysis and Security Countermeasure Selection (Boca Raton, FL: CRC Press, 2010)"
              }
            ]
          },
          {
            "separator": " ",
            "items": [
              {
                "text": "(2)"
              },
              {
                "text": "ASIS International, Protection of Assets: Security Officer Operations (Alexandria, VA: ASIS International, 2011)"
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "Pennsylvania State Police, Domestic Security Division, Risk and Vulnerability Assessment Team, School Safety Report, 2012, accessed April 26, 2018"
              },
              {
                "link": "https://www.psp.pa.gov/public-safety/Documents/RVAT%20School%20Safety%20Report_FINAL%20v1.2.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "security_force_panic": {
    "key": "security_force_panic",
    "prompt": "Does the school security force or staff and faculty have communications, self-defense equipment, restraints, campus transportation (e.g., scooters, bicycles), or panic alarms?",
    "options": [
      {
        "value": "Very_Low",
        "label": "The school has no panic alarms or similar equipment and can only call 9-1-1 during an event."
      },
      {
        "value": "Low",
        "label": "The school may have panic alarms in the administrative office but no other similar equipment."
      },
      {
        "value": "Medium",
        "label": "The security force has some gaps in equipment (e.g., radio, self-defense equipment, restraints, campus transportation), but generally has the minimum essential to perform its mission. Panic buttons are available to administrative staff and in isolated or hard-to-reach areas of the school. "
      },
      {
        "value": "High",
        "label": "The security force has some gaps in equipment (e.g., radio, self-defense equipment, restraints, campus transportation), but generally has the minimum essential to perform its mission. Panic buttons are assigned to administrative staff and some faculty. They are also located in isolated or hard-to-reach areas of the school."
      },
      {
        "value": "Very_High",
        "label": "The security force has no significant gaps in equipment (e.g., radio, self-defense equipment, restraints, body armor, and campus transportation). Panic buttons, including portable units, are located throughout the school."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Investigate the cost and use of panic alarms for key personnel or critical locations. This may include administrative staff and main office locations that may be the first to determine that an attack is occurring. ",
      "Low": "Expand the use of panic alarms to include faculty and isolated or sensitive locations at the school. This may include portable classrooms, gyms, pools, outdoor areas, and labs. ",
      "Medium": "Ensure the security force (employed or contracted) has all equipment required to perform the functions of the job. This may include radios, restraints, non-lethal weapons, and possibly campus transportation. Ensure panic buttons or duress alarms are assigned to administrative staff and some faculty. Place panic buttons or duress alarms in isolated or hard-to-reach areas of the school, such as portable classrooms, gyms, pools, outdoor areas, and labs.",
      "High": "Ensure the security force (employed or contracted) has all equipment required to perform the functions of the job. This may include radios, restraints, non-lethal weapons, and possibly campus transportation. Ensure panic buttons or duress alarms are located throughout the school, and provide portable alarms. Ensure remote or isolated areas of the school, such as portable classrooms, gyms, pools, outdoor areas, and labs, also have alarms.",
      "Very_High": "Establish and implement a policy to test, replace, and repair the equipment as needed. "
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "items": [
              {
                "text": "The ability to communicate and respond during an incident is crucial to first responders and those involved in the incident. Panic alarms may aid in the evaluation of how widespread an attack is. In some cases, adding radios or even some sort of self-defense equipment may help save lives. These elements may be particularly relevant where portable buildings are in use or on campuses with extensive outdoor areas located far from protective cover."
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "items": [
              {
                "text": "ASIS International, Protection of Assets: Security Officer Operations (Alexandria, VA: ASIS International, 2011)."
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "Johns Hopkins University Applied Physics Laboratory, A Comprehensive Report on School Safety Technology, prepared for DOJ National Institute of Justice (NIJ), October 2016"
              },
              {
                "link": "https://www.ojp.gov/pdffiles1/nij/grants/250274.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "command_post": {
    "key": "command_post",
    "prompt": "Has the school designated a command post for the security force?",
    "options": [
      {
        "value": "Very_Low",
        "label": "The school has no areas designated for a security force or law enforcement to use as a command post. The administration office is the assumed the central command area."
      },
      {
        "value": "Low",
        "label": "The school has no designated area for the security force or law enforcement to use as a command post, but the administration office has been used during other events or drills. "
      },
      {
        "value": "Medium",
        "label": "The school has an assigned office area for the security force to use as a command post. "
      },
      {
        "value": "High",
        "label": "A dedicated security force command post exists within the school district. "
      },
      {
        "value": "Very_High",
        "label": "A dedicated security force command post exists at the school."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Designate a primary area where key personnel can gather and assist law enforcement. In some cases, the primary location may be within the area of attack; establish alternates. ",
      "Low": "Provide first responders a specific reporting location and ensure key personnel are available or can access that area. Provide an alternate secure area in another part of the building or offsite. The primary location may be within the area of attack, thus an alternate offsite at a reasonable distance away may become the primary site.",
      "Medium": "Establish a command post for the security force at the district level. Assign, create, or otherwise designate a specific office area at the school that could be accessed easily during an event. Establish a secondary location offsite at a reasonable distance away since the primary may be the site of the attack. ",
      "High": "Establish a command post for the security force at the district level. Assign, create, or otherwise designate a specific office area at the school that could be accessed easily during an event. Establish a secondary location offsite at a reasonable distance away since the primary may be the site of the attack.",
      "Very_High": "Establish a secondary location offsite at a reasonable distance since the primary may be the site of the attack."
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "items": [
              {
                "text": "A command post (also known as a security office, dispatch, or security control center, among other names) serves as the control point for security operations. It may serve as a central monitoring and assessment space for access control and surveillance/intrusion detection systems. The school’s command post is likely distinct from an incident command post that first responders would establish in incident response under the Incident Command System."
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "items": [
              {
                "text": "U.S. Department of the Army, Physical Security (ATTP 3-39.32 / FM 3-19.30), August 3, 2010, accessed April 25, 2018"
              },
              {
                "link": "https://fas.org/irp/doddir/army/attp3-39-32.pdf"
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "ASIS International, Protection of Assets: Security Officer Operations (Alexandria, VA: ASIS International, 2011)"
              }
            ]
          },
          {
            "items": [
              {
                "text": "“National Incident Management System,” FEMA, August 10, 2015"
              },
              {
                "link": "https://training.fema.gov/nims/"
              }
            ]
          }
        ]
      }
    ]
  },
  "monitor_vulnerablity": {
    "key": "monitor_vulnerablity",
    "prompt": "Does the school assign personnel to monitor times of critical vulnerability (i.e., beginning/end of school day, passing periods, and after-school events)?",
    "options": [
      {
        "value": "Very_Low",
        "label": "No personnel actively monitor times of critical vulnerability. Faculty and staff, security personnel, and/or SRO may be present, but no one is specifically assigned to monitor activities. "
      },
      {
        "value": "Medium",
        "label": "Personnel are assigned to monitor times of critical vulnerability. However, they have received little to no training for this position and have no special equipment."
      },
      {
        "value": "Very_High",
        "label": "Well-trained, well-equipped personnel monitor times of critical vulnerability."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Assign personnel to monitor activities during these periods.",
      "Medium": "Provide training to monitoring personnel. Relevant topics may include intruder response, reverse evacuation, and how to assist in the arrival of public safety vehicles. Provide personnel with a radio to communicate with building/office staff and ensure they have access to a phone for calling 9-1-1.",
      "Very_High": "Encourage continuity of monitoring personnel to facilitate positive rapport with students. In addition to serving a security purpose, monitors can help foster a sense of community, identify and prevent bullying, connect with students, and have an overall positive impact on the student body."
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "items": [
              {
                "text": "Actively monitoring activity includes observing interactions, watching for suspicious or unusual activity, and providing an alert presence that may provide for early observation of a possible attack or may deter an attack entirely."
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "items": [
              {
                "text": "Lawrence J. Fennelly and Marianna A. Perry, The Handbook for School Safety and Security: Best Practices and Procedures (Amsterdam, The Netherlands: Elsevier 2014)"
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "DOJ Bureau of Justice Assistance and International Association of Chiefs of Police, Guide for Preventing and Responding to School Violence, 2nd Edition, 2012"
              },
              {
                "link": "https://bja.ojp.gov/sites/g/files/xyckuh186/files/Publications/IACP_School_Violence.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "share_external_agencies": {
    "key": "share_external_agencies",
    "prompt": "Does the school share or exchange security and threat information with external agencies?",
    "options": [
      {
        "value": "Very_Low",
        "label": "The school does not exchange security and threat information with local law enforcement."
      },
      {
        "value": "Low",
        "label": "The school has a relationship with local law enforcement, but contact is irregular."
      },
      {
        "value": "Medium",
        "label": "At least one faculty or staff member regularly meets or conducts calls with a point of contact in the local police department. The school or district does not have a relationship with local or state emergency management agencies, Federal Bureau of Investigation (FBI), or U.S. Secret Service (USSS). "
      },
      {
        "value": "High",
        "label": "The school has a strong relationship with local law enforcement. The school shares information regarding troubled current or former students. A district-level employee has relationships with local or state emergency management agencies, FBI, and USSS. "
      },
      {
        "value": "Very_High",
        "label": "The school has strong relationships with local law enforcement through regular school visits or employment agreements. The school carefully tracks information regarding troubled current or former students. School personnel maintain strong relationships with local or state emergency management, FBI, and USSS. They may regularly attend meetings or conduct calls with representatives of these agencies. "
      }
    ],
    "thingsToConsider": {
      "Very_Low": "At a minimum, ensure that the school has a relationship with local law enforcement and a point of contact in the local police department.",
      "Low": "Enhance information exchange with local law enforcement. Assign at least one faculty or staff member to regularly meet or conduct calls with a point of contact in the local police department.",
      "Medium": "Ensure information regarding troubled current or former students is shared. Establish a liaison with local or state emergency management agencies, the FBI, and USSS.",
      "High": "Explore opportunities to strengthen relationships with local law enforcement through regular school visits or employment agreements. Deepen relationships with local or state emergency management agencies, FBI, and USSS by regularly attending meetings, conducting calls, and/or using resources made available through these agencies.",
      "Very_High": "Continue relationship building and using resources made available through these agencies."
    },
    "moreInfo": [
      {
        "title": "References",
        "body": [
          {
            "items": [
              {
                "text": "DOJ Bureau of Justice Assistance and International Association of Chiefs of Police, Guide for Preventing and Responding to School Violence, 2nd Edition, 2012, accessed April 25, 2018"
              },
              {
                "link": "https://bja.ojp.gov/sites/g/files/xyckuh186/files/Publications/IACP_School_Violence.pdf"
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "DOJ FBI, Violence Prevention in Schools: Enhancement Through Law Enforcement Partnerships, March 2017"
              },
              {
                "link": "https://www.fbi.gov/file-repository/violence-prevention-in-schools-march-2017.pdf/view"
              }
            ]
          },
          {
            "items": [
              {
                "text": "DOJ Office of Community Oriented Policing Services, Beyond the Badge: Profile of a School Resource Officer - A Guide for Law Enforcement, October 2016"
              },
              {
                "link": "https://cops.usdoj.gov/RIC/Publications/cops-p358-pub.pdf"
              }
            ]
          },
          {
            "items": [
              {
                "text": "DOJ Office of Community Oriented Policing Services, Beyond the Badge: Profile of a School Resource Officer - A Guide for School Communities, October 2016"
              },
              {
                "link": "https://cops.usdoj.gov/RIC/Publications/cops-p357-pub.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "exterior_doors": {
    "key": "exterior_doors",
    "prompt": "Does the construction of exterior doors deter or delay an attack?",
    "options": [
      {
        "value": "Very_Low",
        "label": "The school’s main doors are tempered glass with a metal frame. At least half the door is glass. Other doors may or may not have glass, and other exterior doors may be hollow steel."
      },
      {
        "value": "Medium",
        "label": "The school’s main doors are steel, but nearly half the door is tempered or reinforced glass. Other exterior doors may be hollow steel with or without windows."
      },
      {
        "value": "Very_High",
        "label": "The school’s main doors are double-wall steel with at most a small skylight of reinforced glass. Breaking the glass will not easily allow access to a door latch. Other exterior doors are similar and do not have windows."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Explore the option of replacing vulnerable glass with solid doors, which would reduce the opportunity for a person to defeat doors even when they are locked. In an active-shooter event, solid doors may provide more protection. In some cases, it may be possible to retrofit glass doors with bullet-resistant glass or bullet-resistant film. If the school has portable classrooms, available options for improving existing doors may be limited.",
      "Medium": "Where possible, replace any glass doors with steel doors. In some cases, retrofitting glass doors with bullet-resistant glass or bullet-resistant film may be possible. If the school has portable classrooms, available options for improving existing doors may be limited.",
      "Very_High": "If the school has portable classrooms, available options for improving existing doors may be limited. Evaluate the security of doors on portable buildings and improve where feasible. Assess the vulnerabilities inherent to portable buildings, which provide minimal protection against natural or manmade hazards. Explore options to reduce or eliminate the need for portable buildings."
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "items": [
              {
                "text": "To deter is to discourage an action or prevent an occurrence. To delay is to slow down an intruder enough to force them to give up or to provide enough time for an effective response."
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "items": [
              {
                "text": "Barbara A. Nadel, Building Security: Handbook for Architectural Planning and Design (New York, NY: McGraw-Hill 2004)"
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "DHS, Buildings and Infrastructure Protection Series: Primer to Design Safe School Projects in Case of Terrorist Attacks and School Shootings (FEMA-428/BIPS-07), January 2012, "
              },
              {
                "link": "https://www.dhs.gov/xlibrary/assets/st/bips07_428_schools.pdf"
              }
            ]
          },
          {
            "items": [
              {
                "text": "New Jersey School Security Task Force, New Jersey School Security Task Force Report and Recommendations, July 2015, "
              },
              {
                "link": "http://www.state.nj.us/education/schools/security/TaskForceReport.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "classroom_doors": {
    "key": "classroom_doors",
    "prompt": "Can classroom doors remain locked if necessary, for example, during an active-shooter incident?",
    "options": [
      {
        "value": "Very_Low",
        "label": "Not all classrooms have lockable doors."
      },
      {
        "value": "Medium",
        "label": "All classrooms have door locks, but all or some must be locked from the outside. This setup could require individuals to move into a danger zone in order to lock a door."
      },
      {
        "value": "Very_High",
        "label": "All classrooms have door locks that can be locked from either side to prevent entry into the classroom, but they cannot be locked to prevent egress from the classroom (in accordance with building and fire code requirements)."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "(1) Add locks so doors can be secured from inside the classroom. Doors should lock automatically or have a simple locking mechanism, such as a button to push in. Consider the effects on ingress and egress by people with disabilities prior to selecting new locks. (2) Explore options to procure devices that provide instant lockdown protection (e.g., security bars that are forced under the door handle; straps or a metal or plastic sleeve that secure the scissoring arms at the top of the door; and physical devices attached to the door and an anchor point on the floor, doorframe, or wall). Ensure door barricades comply with requirements of the National Fire Protection Association (NFPA) 101, Life Safety Code (unless state or local laws allow otherwise). Every classroom should be able to serve as a safe haven.",
      "Medium": "Replace locks so doors can be secured from inside the classroom. Doors should lock automatically or have a simple locking mechanism, such as a button to push in. Consider the effects on ingress and egress by people with disabilities prior to selecting new locks. (2) Explore options to procure devices that provide instant lockdown protection (e.g., security bars that are forced under the door handle; straps or a metal or plastic sleeve that secure the scissoring arms at the top of the door; and physical devices attached to the door and an anchor point on the floor, doorframe, or wall). Ensure door barricades comply with requirements of the NFPA 101, Life Safety Code (unless state or local laws allow otherwise). Every classroom should be able to serve as a safe haven.",
      "Very_High": "Since door locks provide adequate lockdown protection, explore opportunities to increase protection. (1) Conduct a door survey to evaluate whether door construction materials are appropriate. Metal or solid wood doors provide a higher level of protection. Classroom doors are often designed with view panels, but these may allow an attacker to scope out potential victims. (2) Conduct a window survey to identify exterior windows that do not open. These windows would prevent students in a classroom situation from climbing out if a door breach was imminent. Overall, school officials should consider the age of their students, the desired function of additional updates, and the security and convenience issues of each function. (3) Explore the option of implementing an automatic door-locking system that would allow the school to lock doors remotely."
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "items": [
              {
                "text": "A lockdown device is a piece of hardware applied to a door to facilitate the lockdown process or to prevent an intruder from opening the door. It is generally stored near the door until it is needed, at which time an individual must move it into position."
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "items": [
              {
                "text": "Johns Hopkins University Applied Physics Laboratory, A Comprehensive Report on School Safety Technology, prepared for DOJ NIJ, October 2016, accessed April 24, 2018"
              },
              {
                "link": "https://www.ojp.gov/pdffiles1/nij/grants/250274.pdf"
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "“Why Security Professionals Oppose Classroom Door Barricades,” National Security Solutions, 2018"
              },
              {
                "link": "https://northeastsecuritysolutions.com/why-security-professionals-oppose-classroom-door-barricades/"
              }
            ]
          },
          {
            "items": [
              {
                "text": "DHS, Buildings and Infrastructure Protection Series: Primer to Design Safe School Projects in Case of Terrorist Attacks and School Shootings (FEMA-428/BIPS-07), January 2012"
              },
              {
                "link": "https://www.dhs.gov/xlibrary/assets/st/bips07_428_schools.pdf"
              }
            ]
          },
          {
            "items": [
              {
                "text": "In Defense of Our Schools: Restricting Access to Classrooms Using Door Locks"
              },
              {
                "link": "http://osfm.fire.ca.gov/codedevelopment/pdf/SLTF/35.%20Restrict%20Access%20to%20Classroom%20Using%20Door%20Locks.pdf"
              }
            ]
          },
          {
            "items": [
              {
                "text": "NFPA, NFPA 101: Life Safety Code, 2018"
              },
              {
                "link": "https://www.nfpa.org/codes-and-standards/all-codes-and-standards/list-of-codes-and-standards/detail?code=101"
              }
            ]
          }
        ]
      }
    ]
  },
  "locks": {
    "key": "locks",
    "prompt": "What type of locks does the school use for interior and exterior doors?",
    "options": [
      {
        "value": "Very_Low",
        "label": "All lockable doors have key cylinder locks."
      },
      {
        "value": "Medium",
        "label": "Exterior doors have key cylinder locks or access card controls. "
      },
      {
        "value": "Very_High",
        "label": "Exterior doors have access card controls. "
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Explore options to provide access card controls for at least all exterior doors. Access card systems simplify access control and eliminate problems associated with lost keys.",
      "Medium": "Conduct a survey to identify exterior doors and interior sensitive areas without access card controls. Update entry controls to require access cards for all exterior doors.",
      "Very_High": "Conduct a survey to identify interior sensitive areas without access card controls. Ensure strict access card policies and procedures are in place to reduce the risk of misuse or compromised access control."
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "items": [
              {
                "text": "Various types of access cards exist, as do a number of different types of card readers. Common access cards include magnetic stripe, Wiegand wire, passive proximity, active proximity, and smart cards (both touch and touchless types). Consult the sources below for more information about these types of access cards and others."
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "items": [
              {
                "text": "Lawrence Fennelly, Effective Physical Security, 4th Edition (Waltham, MA: Elsevier 2012)."
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "“DHS, Buildings and Infrastructure Protection Series: Primer to Design Safe School Projects in Case of Terrorist Attacks and School Shootings (FEMA-428/BIPS-07), January 2012"
              },
              {
                "link": "https://www.dhs.gov/xlibrary/assets/st/bips07_428_schools.pdf"
              }
            ]
          },
          {
            "items": [
              {
                "text": "Johns Hopkins University Applied Physics Laboratory, A Comprehensive Report on School Safety Technology, prepared for DOJ NIJ, October 2016, accessed April 24, 2018"
              },
              {
                "link": "https://www.ojp.gov/pdffiles1/nij/grants/250274.pdf"
              }
            ]
          },
          {
            "items": [
              {
                "text": "Thomas L. Norman, Electronic Access Control (Waltham, MA: Elsevier 2012)."
              }
            ]
          }
        ]
      }
    ]
  },
  "double_doors": {
    "key": "double_doors",
    "prompt": "Do exterior double doors have handles that can be tied or chained together to prevent emergency evacuation or access by first responders?",
    "options": [
      {
        "value": "Very_Low",
        "label": "Exterior double doors have handles that could be tied or chained together to prevent egress or first responder access."
      },
      {
        "value": "Medium",
        "label": "The hardware on exterior double doors has been removed or replaced so the doors cannot be chained or tied together. Interior double doors have not been addressed."
      },
      {
        "value": "Very_High",
        "label": "The hardware on both exterior and interior double doors has been removed or replaced so the doors cannot be chained or tied together."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Conduct a door survey to identify all exterior double doors with handles susceptible to misuse. Remove the left-side handles so the doors cannot be tied or chained together.",
      "Medium": "Conduct a door survey to identify all interior double doors with handles susceptible to misuse. Focus on large areas such as the gym, theater, and cafeteria. Remove the left-side handles so interior doors cannot be tied or chained together.",
      "Very_High": "Maintain protocols to disallow double doors with handles on both sides, so they cannot be tied or chained together."
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "items": [
              {
                "text": "While removing one handle on a pair of exterior doors makes the doors chain-resistant, it also reduces ingress capability by half. Door designs that provide a flush surface to allow interior locking hardware to be mounted without gaps between exit device crossbars or traditional push bars on doors should be considered during new construction or when doors are replaced."
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "items": [
              {
                "text": "Virginia Tech Review Panel, Mass Shootings at Virginia Tech, April 16, 2007, accessed June 26, 2018"
              },
              {
                "link": "https://scholar.lib.vt.edu/prevail/docs/April16ReportRev20091204.pdf"
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "DHS, Buildings and Infrastructure Protection Series: Primer to Design Safe School Projects in Case of Terrorist Attacks and School Shootings (FEMA-428/BIPS-07), January 2012"
              },
              {
                "link": "https://www.dhs.gov/xlibrary/assets/st/bips07_428_schools.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "id_cards_staff": {
    "key": "id_cards_staff",
    "prompt": "Does the school issue identification (ID) cards to faculty and staff members?",
    "options": [
      {
        "value": "Very_Low",
        "label": "Faculty and staff do not have IDs of any kind."
      },
      {
        "value": "Low",
        "label": "Faculty and staff have non-photo IDs."
      },
      {
        "value": "Medium",
        "label": "Faculty and staff have photo IDs. They are not required to display them, or the school does not strictly enforce this requirement. Photo IDs do not function as access cards."
      },
      {
        "value": "High",
        "label": "Faculty and staff are issued school-specific photo IDs that they must display at all times. Cards are coded to limit access to specific areas within the school. School administration personnel can immediately change access to sensitive areas."
      },
      {
        "value": "Very_High",
        "label": "Faculty and staff are issued school-specific photo IDs that they must display at all times. IDs are used to access the building through the use of a card reader or turnstile. Cards are coded to limit access to specific areas within the school. School administration personnel can immediately change access to sensitive areas."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Issue IDs to all employees.",
      "Low": "Replace non-photo IDs with photo IDs.",
      "Medium": "Require faculty and staff to display their IDs at all times, and enforce ID requirements consistently. Explore options to replace the current ID system with an access card entry control system.",
      "High": "Evaluate the need to issue a requirement for faculty and staff to use their IDs to access the building (i.e., via the use of a card reader or turnstile).",
      "Very_High": "Regularly review the access control ID card database and activity reports for sensitive areas."
    },
    "moreInfo": [
      {
        "title": "References",
        "body": [
          {
            "items": [
              {
                "text": "Regularly review the access control ID card database and activity reports for sensitive areas."
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "Johns Hopkins University Applied Physics Laboratory, A Comprehensive Report on School Safety Technology, prepared for DOJ NIJ, October 2016"
              },
              {
                "link": "https://www.ojp.gov/pdffiles1/nij/grants/250274.pdf"
              }
            ]
          },
          {
            "items": [
              {
                "text": "New Jersey School Security Task Force, New Jersey School Security Task Force Report and Recommendations, July 2015"
              },
              {
                "link": "https://www.state.nj.us/education/schools/security/TaskForceReport.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "id_cards_students": {
    "key": "id_cards_students",
    "prompt": "Does the school issue student ID cards?",
    "options": [
      {
        "value": "Very_Low",
        "label": "Students do not have IDs of any kind."
      },
      {
        "value": "Low",
        "label": "Students may have non-photo IDs."
      },
      {
        "value": "Medium",
        "label": "Students have photo IDs. They are not required to display them, or this requirement is not strictly enforced. Photo IDs do not function as access  cards."
      },
      {
        "value": "High",
        "label": "Students are issued school-specific photo IDs that they must display at all times. Cards are coded to limit access to specific areas within the school. School administration personnel can immediately change access to sensitive areas."
      },
      {
        "value": "Very_High",
        "label": "Students are issued school-specific photo IDs that must be displayed at all times. IDs are used to access the building through the use of a card reader or turnstile. Cards are coded to limit access to specific areas within the school. School administration personnel can immediately change access to sensitive areas."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Issue IDs to all students.",
      "Low": "Replace non-photo IDs with photo IDs.",
      "Medium": "Require students to display their IDs at all times, and enforce ID requirements consistently. Explore options to replace the current ID system with an access card entry control system.",
      "High": "Evaluate the need to issue a requirement for students to use their IDs to access the building (i.e., via the use of a card reader or turnstile).",
      "Very_High": "Regularly review the access control ID card database and activity reports for sensitive areas to identify unauthorized access or suspicious activity."
    },
    "moreInfo": [
      {
        "title": "References",
        "body": [
          {
            "items": [
              {
                "text": "Lawrence J. Fennelly and Marianna A. Perry, The Handbook for School Safety and Security: Best Practices and Procedures (Amsterdam, The Netherlands: Elsevier 2014)."
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "Johns Hopkins University Applied Physics Laboratory, A Comprehensive Report on School Safety Technology, prepared for DOJ NIJ, October 2016"
              },
              {
                "link": "https://www.ojp.gov/pdffiles1/nij/grants/250274.pdf"
              }
            ]
          },
          {
            "items": [
              {
                "text": "New Jersey School Security Task Force, New Jersey School Security Task Force Report and Recommendations, July 2015"
              },
              {
                "link": "https://www.state.nj.us/education/schools/security/TaskForceReport.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "access_after_school": {
    "key": "access_after_school",
    "prompt": "Is access to after-school events controlled?",
    "options": [
      {
        "value": "Very_Low",
        "label": "The school does not control access to after-school events."
      },
      {
        "value": "Low",
        "label": "After-school events do not include a package inspection. At some large events, law enforcement may suggest restrictions on items that participants bring into the building. "
      },
      {
        "value": "Medium",
        "label": "After-school events may include a package inspection or some restrictions on what can be brought in so that event personnel (e.g., student workers, volunteers) can identify something suspicious. Training for event personnel may be limited to verbal instructions. Magnetometer or equivalent may be used for some large events or when law enforcement recommends it. Most exterior doors are locked to limit the number of available access points to a few designated doors. "
      },
      {
        "value": "High",
        "label": "After-school events such as plays, concerts, and games require a ticket (fee or no fee) and presenting the ticket in person to event personnel. Well-trained event personnel inspect bags and may use magnetometers or an equivalent for large events such as football games and theater performances. Most exterior doors are locked."
      },
      {
        "value": "Very_High",
        "label": "After-school events such as plays, concerts, and games require a ticket (fee or no fee) and presenting the ticket in person to a trained member of the security staff. Well-trained event personnel inspect bags and may use magnetometers or an equivalent. All doors are locked except the minimum required based on event size as determined in consultation with the fire marshal. "
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Collaborate with local law enforcement on restrictions for items brought into large events.",
      "Low": "Randomly inspect items brought into after-school events. Establish a list of prohibited items, including potential weapons. Post restrictions to inform attendees of restricted items. Use magnetometers or an equivalent for large events, based on guidance that local law enforcement provides.",
      "Medium": "Require attendees to present a ticket (fee or no fee) to a person. Inspect all bags. Use magnetometers or an equivalent for large events. Review the number of available access points during events to determine if the school should make fewer available for entry.",
      "High": "Use trained security to take tickets and inspect bags. Consult with the fire marshal to evaluate the need to further limit the number the number of unlocked doors based on event size.",
      "Very_High": "Continue access control measures for after-school events. Periodically review policies and procedures to assess whether improvements are needed."
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "items": [
              {
                "text": "After-school events may include school-sponsored athletics or performing arts or meetings and activities held by non-school groups. After-schools events may provide opportunities for an attacker to hide weapons or retrieve a previously hidden weapon. Controlling access to an event is as important as controlling access within the building and limiting access whenever possible (for example, by securing classrooms and other areas not needed for the event)."
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "items": [
              {
                "text": "Lawrence Fennelly, Effective Physical Security, 4th Edition (Waltham, MA: Elsevier 2012)."
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "“Large Event Preparedness,” Colorado Department of Public Safety, School Safety Resource Center, 2018"
              },
              {
                "link": "https://cssrc.colorado.gov/large-event-preparedness"
              }
            ]
          },
          {
            "items": [
              {
                "text": "Stephen Lopez, “7 Steps to Improving Stadium and Large Event Security,” Campus Safety, June 21, 2017"
              },
              {
                "link": "https://www.campussafetymagazine.com/emergency/7-steps-to-improving-stadium-and-large-event-security/"
              }
            ]
          },
          {
            "items": [
              {
                "text": "DOJ, Office of Community Oriented Policing Services, Planning and Managing Security for Major Special Events: Guidelines for Law Enforcement, March 2007"
              },
              {
                "link": "https://cops.usdoj.gov/RIC/Publications/cops-w0703-pub.pdf"
              }
            ]
          },
          {
            "items": [
              {
                "text": "Commercial Facilities Sector-Specific Agency, Patron Screening Best Practices Guide, March 2016"
              },
              {
                "link": "https://www.cisa.gov/sites/default/files/publications/patron-screening-guide-03-16-508.pdf"
              }
            ]
          },
          {
            "items": [
              {
                "text": "ISC, Items Prohibited from Federal Facilities: An Interagency Security Committee Standard, February 2013"
              },
              {
                "link": "https://www.cisa.gov/sites/default/files/publications/isc-items-prohibited-federal-facilities-feb-2013-508.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "control_visitor_access": {
    "key": "control_visitor_access",
    "prompt": "Does the school control visitor access?",
    "options": [
      {
        "value": "Very_Low",
        "label": "The school has no visitor controls. The school may have a record of visitor activity such as a sign-in sheet."
      },
      {
        "value": "Low",
        "label": "Signs direct visitors to the school (i.e., parents or anyone other than faculty and staff) to use the main entry. Visitors must sign in (no ID required), and administration or student helpers escort them to appropriate areas."
      },
      {
        "value": "Medium",
        "label": "Visitors to the school (i.e., parents or anyone other than faculty and staff) are physically not allowed to enter the school beyond the initial main entry. They must show a photo ID to school administration to confirm they are whom they claim to be. However, the school does not have a visitor management system to screen visitors. Visitors are asked to wait in the secured lobby, or security or administration personnel may escort them to appropriate areas."
      },
      {
        "value": "High",
        "label": "Visitors to the school (i.e., parents or anyone other than faculty and staff) can access an initial set of doors but cannot pass into an administration area (i.e., mantrap). Visitors must show a government-issued photo ID to a member of the security force or school administration, who may use a visitor management system to search databases (e.g., sex offender lists, banned visitors) for any concerns in a visitor’s background. Permitted visitors may receive a printed badge. Security or administration personnel escort them to a secure holding room or to appropriate areas within the school. They must remain with an escort at all times."
      },
      {
        "value": "Very_High",
        "label": "Visitors to the school (i.e., parents or anyone other than faculty and staff) can access an initial set of doors but cannot pass into an administration area (i.e., mantrap). Visitors must provide a government-issued photo ID to a member of the security force, who then uses a visitor management system to check designated databases (e.g., sex offender lists, banned visitors) for any concerns in a visitor’s background. Permitted visitors receive a badge. Security personnel escort them to a secure holding room or to appropriate areas within the school. They must remain with the security escort at all times."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Require visitors to sign in and sign out and wear a visible visitor's badge.",
      "Low": "Implement measures to stop visitors from entering the school beyond the initial main entry. Require visitors to provide photo ID. Discontinue the practice of using student helpers to escort visitors, and use only administration or security personnel.",
      "Medium": "Implement measures to further control and delay a visitor's initial access to the school, for example, by allowing access to an initial set of doors only but not the administration area. Require visitors to provide a government-issued photo ID. Explore the option of procuring a visitor management system capable of screening visitors against databases such as sex offender lists, banned visitors, etc. Escort visitors at all times.",
      "High": "Employ security personnel, not administrative personnel, to control visitor access. If the school does not have a visitor management system, explore the option of procuring one. Implement procedures to print badges for visitors to wear while at the school.",
      "Very_High": "Continue to implement strict visitor access controls. Periodically review policies and procedures to assess whether improvements are needed."
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "items": [
              {
                "text": "(1) A mantrap is a small room designed to confine individuals before they are allowed or denied access into a facility or secure area. This “trap” enables the review and verification of an individual’s credentials. Mantraps, sometimes called security vestibules, usually have two doors: one to the secure area, and the other from the non-secured area. (2) A visitor management system is capable of screening visitors using a government- or school-issued ID. The system will then search against databases that the school designates (e.g., sex offender lists, banned visitors) for any concerns in the visitor’s background. If the visitor passes the system’s screening, it will print a badge for him/her to wear while at the school. This system can track and maintain a database of visitors over time."
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "items": [
              {
                "text": "DHS, Buildings and Infrastructure Protection Series: Primer to Design Safe School Projects in Case of Terrorist Attacks and School Shootings (FEMA-428/BIPS-07), January 2012, accessed April 24, 2018"
              },
              {
                "link": "https://www.dhs.gov/xlibrary/assets/st/bips07_428_schools.pdf"
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "Lawrence J. Fennelly and Marianna A. Perry, The Handbook for School Safety and Security: Best Practices and Procedures (Amsterdam, The Netherlands: Elsevier 2014)."
              }
            ]
          },
          {
            "items": [
              {
                "text": "Randy Atlas, “8 Ways to Improve School Entrance Security,” Campus Safety, March 23, 2017"
              },
              {
                "link": "https://www.campussafetymagazine.com/hospital/8_ways_to_improve_school_entrance_security/"
              }
            ]
          },
          {
            "items": [
              {
                "text": "New Jersey School Security Task Force, New Jersey School Security Task Force Report and Recommendations, July 2015"
              },
              {
                "link": "https://www.state.nj.us/education/schools/security/TaskForceReport.pdf"
              }
            ]
          },
          {
            "items": [
              {
                "text": "Tod Schneider, School Security Technologies, National Institute of Building Sciences, National Clearinghouse for Educational Facilities, January 2010"
              },
              {
                "link": "https://files.eric.ed.gov/fulltext/ED507917.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "student_items_inspected": {
    "key": "student_items_inspected",
    "prompt": "Are items that students bring into the school inspected (e.g., backpacks, bags)?",
    "options": [
      {
        "value": "Very_Low",
        "label": "The school does not inspect items that students bring into the school, or it inspects items brought onsite only if there is cause."
      },
      {
        "value": "Medium",
        "label": "The school randomly inspects items students bring onsite."
      },
      {
        "value": "Very_High",
        "label": "Items are inspected, and may go through x-ray screening."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Train personnel on potential indicators that a student may be attempting to bring a weapon onsite. Institute random inspections.",
      "Medium": "Inspect all items students bring onsite. Explore options to provide x-ray screening for all backpacks, bags, etc.",
      "Very_High": "Continue to inspect all items brought onsite. Periodically review policies and procedures to assess whether improvements are needed."
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "items": [
              {
                "text": "School personnel are encouraged to seek legal advice before implementing any policy on the search or seizure of students’ property. While a warrant may not be required, school officials must have valid justification for any search or seizure. The sources below include additional information on this topic."
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "items": [
              {
                "text": "DOJ Bureau of Justice Assistance and International Association of Chiefs of Police, Guide for Preventing and Responding to School Violence, 2nd Edition, 2012, accessed April 25, 2018"
              },
              {
                "link": "https://bja.ojp.gov/sites/g/files/xyckuh186/files/Publications/IACP_School_Violence.pdf"
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "Kate R. Ehlenberger, “The Right to Search Students,” Understanding the Law, December 2001/January 2002, Vol. 59, No. 4"
              },
              {
                "link": "http://www.ascd.org/publications/educational-leadership/dec01/vol59/num04/The-Right-to-Search-Students.aspx"
              }
            ]
          },
          {
            "items": [
              {
                "text": "“School Discipline Laws & Regulations by Category: In-School Discipline, Use of student and locker searches,” National Center on Safe Supportive Learning Environments"
              },
              {
                "link": "https://safesupportivelearning.ed.gov/discipline-compendium/choose-state?field_sub_category_value=Use+of+student+and+locker+searches"
              },
              {
                "text": "Select the state or jurisdiction(s) for which you would like information on laws and regulations within your selected sub-category and click “Apply” at the bottom of the page"
              }
            ]
          },
          {
            "items": [
              {
                "text": "Minnesota Department of Public Safety, Division of Homeland Security and Emergency Management, Minnesota School Safety Center Program, Comprehensive School Safety Guide, 2011"
              },
              {
                "link": "https://dps.mn.gov/divisions/hsem/mn-school-safety-center/Documents/comprehensive-school-safety-guide.pdf"
              }
            ]
          },
          {
            "items": [
              {
                "text": "New Jersey School Security Task Force, New Jersey School Security Task Force Report and Recommendations, July 2015"
              },
              {
                "link": "https://www.state.nj.us/education/schools/security/TaskForceReport.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "vistor_bags_inspected": {
    "key": "vistor_bags_inspected",
    "prompt": "Are bags and packages belonging to visitors (i.e., parents or anyone other than faculty and staff) inspected as they are brought into the school?",
    "options": [
      {
        "value": "Very_Low",
        "label": "The school does not inspect bags or packages belonging to visitors. The school does not question persons bringing items into the building."
      },
      {
        "value": "Low",
        "label": "The school does not inspect visitors’ bags and packages. Administration questions what is being brought in and may hold the items at the administration office."
      },
      {
        "value": "Medium",
        "label": "Visitors to the school may have a bag or package inspected, but it would be a rare occurrence. The school holds all items at the administration office for pickup."
      },
      {
        "value": "High",
        "label": "The security force or administration personnel inspect all bags and packages that visitors bring onsite."
      },
      {
        "value": "Very_High",
        "label": "A trained member of the security force inspects all bags and packages that visitors bring onsite."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Train personnel on potential indicators that a visitor may be attempting to bring a weapon onsite.",
      "Low": "Post signs advising visitors that items brought onsite may be inspected. Institute policies requiring all items to be held at the administration office for pickup.",
      "Medium": "Inspect all packages and bags that visitors bring onsite.",
      "High": "Employ security personnel to inspect all packages and bags that visitors bring onsite.",
      "Very_High": "Continue bag and package inspections for all visitors. Periodically review policies and procedures to assess whether improvements are needed."
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "items": [
              {
                "text": "School personnel are encouraged to seek legal advice before implementing any policy on the search or seizure of property. While a warrant may not be required, school officials must have valid justification for any search or seizure."
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "items": [
              {
                "text": "DOJ Bureau of Justice Assistance and International Association of Chiefs of Police, Guide for Preventing and Responding to School Violence, 2nd Edition, 2012, accessed April 25, 2018"
              },
              {
                "link": "https://bja.ojp.gov/sites/g/files/xyckuh186/files/Publications/IACP_School_Violence.pdf"
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "Minnesota Department of Public Safety, Division of Homeland Security and Emergency Management, Minnesota School Safety Center Program, Comprehensive School Safety Guide, 2011"
              },
              {
                "link": "https://dps.mn.gov/divisions/hsem/mn-school-safety-center/Documents/comprehensive-school-safety-guide.pdf"
              }
            ]
          },
          {
            "items": [
              {
                "text": "New Jersey School Security Task Force, New Jersey School Security Task Force Report and Recommendations, July 2015"
              },
              {
                "link": "https://www.state.nj.us/education/schools/security/TaskForceReport.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "magnetometers_screen": {
    "key": "magnetometers_screen",
    "prompt": "Does the school use magnetometers or an equivalent to screen students and visitors?",
    "options": [
      {
        "value": "Very_Low",
        "label": "The school does not use magnetometers or equivalent equipment."
      },
      {
        "value": "Low",
        "label": "Magnetometers or an equivalent are not available, but the school is considering purchasing and using them in the future."
      },
      {
        "value": "Medium",
        "label": "Magnetometers or an equivalent are available at main entry points but are seldom, if ever, used."
      },
      {
        "value": "High",
        "label": "Magnetometers or an equivalent are in use at main entry points, and trained members of the security force staff them."
      },
      {
        "value": "Very_High",
        "label": "Magnetometers or an equivalent are in use at all access control points, and trained members of the security force staff them."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Explore the option of making magnetometers or an equivalent available.",
      "Low": "Explore the option of installing magnetometers or an equivalent at main entry points.",
      "Medium": "Use magnetometers or an equivalent regularly. Employ trained members of the security force to perform this function.",
      "High": "Install and use magnetometers at all access control points.",
      "Very_High": "Continue the use of magnetometers or an equivalent. Periodically review policies and procedures to assess whether improvements are needed."
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "items": [
              {
                "text": "A magnetometer is a walk-through metal detector that allows rapid and effective screening for weapons and other contraband"
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "items": [
              {
                "text": "Johns Hopkins University Applied Physics Laboratory, A Comprehensive Report on School Safety Technology, prepared for DOJ NIJ, October 2016, accessed April 24, 2018"
              },
              {
                "link": "https://www.ojp.gov/pdffiles1/nij/grants/250274.pdf"
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "DHS, Buildings and Infrastructure Protection Series: Primer to Design Safe School Projects in Case of Terrorist Attacks and School Shootings (FEMA-428/BIPS-07), January 2012"
              },
              {
                "link": "https://www.dhs.gov/xlibrary/assets/st/bips07_428_schools.pdf"
              }
            ]
          },
          {
            "items": [
              {
                "text": "New Jersey School Security Task Force, New Jersey School Security Task Force Report and Recommendations, July 2015"
              },
              {
                "link": "https://www.state.nj.us/education/schools/security/TaskForceReport.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "Fencing": {
    "key": "Fencing",
    "prompt": "Does the school have fencing?",
    "options": [
      {
        "value": "Very_Low",
        "label": "The school is an open campus without fencing, or the school has fencing, but the fence is weak, old, or in disrepair. There may be holes in the fencing material, weak areas, or gaps between the fencing. The fence does not clearly define the school’s perimeter."
      },
      {
        "value": "Medium",
        "label": "The school has a fence, but it does not enclose the school. It may enclose a playground, part of the school grounds, or a stadium or athletic field."
      },
      {
        "value": "Very_High",
        "label": "Fencing completely encloses the school. No persons can enter any part of the school property without going through a gate in the fence or through the main entrance doors."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "If fencing does not exist, determine what role, if any, fencing should have at the school (e.g., to clearly distinguish the perimeter of the site to protect against trespassing, provide access control by channeling individuals through authorized access points, and/or protect against unauthorized entry by providing increased access delay and more time for assessment). Install fencing that is appropriate for the school's objectives. If fencing exists, repair or replace fencing as needed.",
      "Medium": "Install additional fencing to enclose the facility to the maximum extent possible and/or employ Crime Prevention Through Environmental Design (CPTED) principles and/or barriers (e.g., bollards, decorative flower pots, high curbs, shallow ditches) to provide enhanced penetration delay.",
      "Very_High": "Explore the feasibility of improving the landscape to supplement the existing facility perimeter fence. Landscaping examples include earthen berms, low-growing shrubs, plants, or trees. Regularly assess and maintain the fence. Explore the option of combining elevation changes and/or aggressive plantings with fencing barriers for additional protection. (Aggressive plantings include cacti, thorny bushes, and plants and trees.)"
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "items": [
              {
                "text": "(1)"
              },
              {
                "text": "ISC, Facility Security Plan: An Interagency Security Committee Guide, 1st Edition, February 2015, accessed April 24, 2018"
              }
            ]
          },
          {
            "items": [
              {
                "text": "(2)"
              },
              {
                "text": "CPTED is a multi-disciplinary approach to deterring criminal behavior through environmental design. CPTED strategies rely upon the ability to influence a potential offender’s decisions by affecting the built, social, and administrative environment. CPTED defines three basic strategies for security design: natural access control, natural surveillance, and territorial reinforcement."
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "items": [
              {
                "text": "DHS, Buildings and Infrastructure Protection Series: Primer to Design Safe School Projects in Case of Terrorist Attacks and School Shootings (FEMA-428/BIPS-07), January 2012, accessed April 24, 2018, "
              },
              {
                "link": "https://www.dhs.gov/xlibrary/assets/st/bips07_428_schools.pdf"
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "Johns Hopkins University Applied Physics Laboratory, A Comprehensive Report on School Safety Technology, prepared for DOJ NIJ, October 2016, "
              },
              {
                "link": "https://www.ncjrs.gov/pdffiles1/nij/grants/250274.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "fence_height": {
    "key": "fence_height",
    "prompt": "What is the height of the fence?",
    "options": [
      {
        "value": "Very_Low",
        "label": "The height of the fence is less than 6 feet, or the fence may be 6 feet or higher in some areas but less than 6 feet in other areas."
      },
      {
        "value": "Medium",
        "label": "The fence is no more than 6 feet tall. Note that the height of the fence does not include outriggers, if any exist."
      },
      {
        "value": "Very_High",
        "label": "The fence is at least 7 feet tall. Note that the height of the fence does not include outriggers, if any exist."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Replace the existing fence with higher fencing with a consistent height throughout.",
      "Medium": "Evaluate the need to increase the height of the fence.",
      "Very_High": "Evaluate the need to increase the height of the fence. The higher the fence, the more difficult and time-consuming it is to climb."
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "items": [
              {
                "text": "Skip this question if your school does not have a fence. The height of a fence can determine ease of access onto school property. However, the appropriate height depends on a school’s individual needs."
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "items": [
              {
                "text": "Lawrence J. Fennelly and Marianna A. Perry, The Handbook for School Safety and Security: Best Practices and Procedures (Amsterdam, The Netherlands: Elsevier 2014)."
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "DHS, Buildings and Infrastructure Protection Series: Primer to Design Safe School Projects in Case of Terrorist Attacks and School Shootings (FEMA-428/BIPS-07), January 2012, "
              },
              {
                "link": "https://www.dhs.gov/xlibrary/assets/st/bips07_428_schools.pdf"
              }
            ]
          },
          {
            "items": [
              {
                "text": "Johns Hopkins University Applied Physics Laboratory, A Comprehensive Report on School Safety Technology, prepared for DOJ NIJ, October 2016, "
              },
              {
                "link": "https://www.ncjrs.gov/pdffiles1/nij/grants/250274.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "fence_material": {
    "key": "fence_material",
    "prompt": "What material is the fence made of?",
    "options": [
      {
        "value": "Very_Low",
        "label": "The fence may be a mix of materials, but no section appears more secure than standard chain link. "
      },
      {
        "value": "Medium",
        "label": "The fence is standard-size chain link. Other materials may exist in addition to or in place of the chain-link fence, and define the perimeter. These materials may be hedgerows; small, decorative rock walls; or other materials; and these materials appear as effective as standard chain link."
      },
      {
        "value": "Very_High",
        "label": "The fence is standard-size chain link. Sections of the fence may be an improvement to or appear more secure than standard chain link."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Explore the feasibility of upgrading to stronger fence construction such as chain link.",
      "Medium": "Explore the option of supplementing or enhancing penetration delay anchoring the base of the fence.",
      "Very_High": "Regularly assess and maintain the fence."
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "items": [
              {
                "text": "Skip this question if your school does not have a fence. Chain-link fencing is versatile, inexpensive, and used in many applications. Expanded metal fencing and welded wire fencing may be more secure than chain link but are more expensive. Ornamental fences (iron, steel, or aluminum) are more aesthetically pleasing and provide visibility, but durability and maintenance costs vary greatly."
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "separator": " ",
            "items": [
              {
                "text": "Johns Hopkins University Applied Physics Laboratory, A Comprehensive Report on School Safety Technology, prepared for DOJ NIJ October 2016, accessed April 24, 2018, "
              },
              {
                "link": "https://www.ncjrs.gov/pdffiles1/nij/grants/250274.pdf"
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "Hanover Research, School Fencing: Benefits and Disadvantages, November 2013, "
              },
              {
                "link": "https://wssca.wildapricot.org/resources/Documents/Public%20Site/Links/fencing_safety.pdf"
              }
            ]
          },
          {
            "items": [
              {
                "text": "DHS, Buildings and Infrastructure Protection Series: Primer to Design Safe School Projects in Case of Terrorist Attacks and School Shootings (FEMA-428/BIPS-07), January 2012, "
              },
              {
                "link": "https://www.dhs.gov/xlibrary/assets/st/bips07_428_schools.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "fence_purpose": {
    "key": "fence_purpose",
    "prompt": "What is the purpose of the fence?",
    "options": [
      {
        "value": "Very_Low",
        "label": "The purpose of the fence is more decorative or to define the perimeter, but it does not deter or delay an intruder."
      },
      {
        "value": "Medium",
        "label": "The fence delineates at least part of the perimeter. It provides some level of delay and/or deterrence to intruders."
      },
      {
        "value": "Very_High",
        "label": "The fence is an obvious deterrent and would delay an intrusion attempt."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "If improvements to the fence are not possible, employ CPTED principles.",
      "Medium": "Evaluate all aspects of the fence to identify deficiencies in coverage and install enhancements to improve its effectiveness and penetration delay such as anchoring the base of the fence.",
      "Very_High": "Ensure the fencing is in good repair to maintain deterrence value. Explore the option of supplementing with other deterrence measures such as security cameras."
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "items": [
              {
                "text": "(1)"
              },
              {
                "text": "To deter is to discourage an action or prevent an occurrence. To delay is to slow down an intruder enough to force them to give up or to provide enough time for an effective response."
              }
            ]
          },
          {
            "items": [
              {
                "text": "(2)"
              },
              {
                "text": "CPTED is a multi-disciplinary approach to deterring criminal behavior through environmental design. CPTED strategies rely upon the ability to influence a potential offender’s decisions by affecting the built, social, and administrative environment. CPTED defines three basic strategies for security design: natural access control, natural surveillance, and territorial reinforcement."
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "separator": " ",
            "items": [
              {
                "text": "DHS, Buildings and Infrastructure Protection Series: Primer to Design Safe School Projects in Case of Terrorist Attacks and School Shootings (FEMA-428/BIPS-07), January 2012, accessed April 26, 2018, "
              },
              {
                "link": "https://www.dhs.gov/xlibrary/assets/st/bips07_428_schools.pdf"
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "Hanover Research, School Fencing: Benefits and Disadvantages, November 2013, "
              },
              {
                "link": "https://wssca.wildapricot.org/resources/Documents/Public%20Site/Links/fencing_safety.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "gates": {
    "key": "gates",
    "prompt": "Does the school have gates?",
    "options": [
      {
        "value": "Very_Low",
        "label": "The school does not have any gates. "
      },
      {
        "value": "Medium",
        "label": "Gates exist in some areas but not others. Gates may be drop-arm wood gates that an intruder could easily defeat."
      },
      {
        "value": "Very_High",
        "label": "Gates are used at all entrances. They are well-maintained."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Asses the need to install gates to provide appropriate levels of access control and/or penetration delay against individuals.",
      "Medium": "Install gates at identified pedestrian/vehicle gaps along the perimeter. Ensure that all gates have at least equivalent properties to the rest of the fence.",
      "Very_High": "Ensure gates remain in good repair. Explore the option of providing security camera coverage for all gates."
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "items": [
              {
                "text": "A gate is an opening in the perimeter that allows people or vehicles to pass through at controlled points of entry."
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "separator": " ",
            "items": [
              {
                "text": "DHS, Buildings and Infrastructure Protection Series: Primer to Design Safe School Projects in Case of Terrorist Attacks and School Shootings (FEMA-428/BIPS-07), January 2012, accessed April 26, 2018, "
              },
              {
                "link": "https://www.dhs.gov/xlibrary/assets/st/bips07_428_schools.pdf"
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "Johns Hopkins University Applied Physics Laboratory, A Comprehensive Report on School Safety Technology, prepared for DOJ NIJ, October 2016, "
              },
              {
                "link": "https://www.ncjrs.gov/pdffiles1/nij/grants/250274.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "monitoring_vehicles": {
    "key": "monitoring_vehicles",
    "prompt": "Are vehicles parked at the school screened, monitored, and/or inspected?",
    "options": [
      {
        "value": "Very_Low",
        "label": "The school does not screen, monitor, or inspect any vehicles driven onsite."
      },
      {
        "value": "Low",
        "label": "The school has written policies and procedures for vehicle searches. Signage warns that any vehicle parked on school property is subject to search. The school has no way to identify vehicles parked onsite."
      },
      {
        "value": "Medium",
        "label": "The school has comprehensive vehicle search policies and procedures. Any vehicle parked on campus is subject to search. Drivers are required to display their parking permits or their vehicles will be towed. School personnel do not patrol parking lots."
      },
      {
        "value": "High",
        "label": "Personnel conduct vehicle searches randomly and when suspicious activity is reported. Parking permits are required except in the designated visitor parking area(s). School personnel patrol parking lots, but the school has no camera coverage."
      },
      {
        "value": "Very_High",
        "label": "The security force conducts vehicle searches randomly and when suspicious activity is reported. Parking permits are required. Security patrols the parking lot(s), and the school has camera coverage for all parking areas."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Establish a list of prohibited items, including potential weapons, if one does not already exist. Post signage warning that parking on school property is assumed to be consent to search. Develop policies for searching vehicles.",
      "Low": "Institute procedures for faculty, staff, and students (if applicable) to register their vehicles with the school. Issue numbered and recognizable permits such as mirror hang tags. Contract with a towing company to check the lot and tow unpermitted vehicles.",
      "Medium": "Conduct patrols of parking areas.",
      "High": "Provide camera coverage and adequate lighting for videotaping activity in the parking lots.",
      "Very_High": "Keep the camera system in good repair to ensure continuous coverage of parking areas."
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "items": [
              {
                "text": "School personnel are encouraged to seek legal advice before implementing any policy on the search or seizure of students’ property. While a warrant may not be required, school officials must have valid justification for any search or seizure."
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "items": [
              {
                "text": "Lawrence J. Fennelly and Marianna A. Perry, The Handbook for School Safety and Security: Best Practices and Procedures (Amsterdam, The Netherlands: Elsevier 2014)."
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "DHS, Buildings and Infrastructure Protection Series: Primer to Design Safe School Projects in Case of Terrorist Attacks and School Shootings (FEMA-428/BIPS-07), January 2012, "
              },
              {
                "link": "https://www.dhs.gov/xlibrary/assets/st/bips07_428_schools.pdf"
              }
            ]
          },
          {
            "items": [
              {
                "text": "ISC, Items Prohibited from Federal Facilities: An Interagency Security Committee Standard, February 2013, "
              },
              {
                "link": "https://www.archives.gov/files/research/notices/ISC-%20Items%20Prohibited%20from%20Federal%20Facilities-%20An%20ISC%20Standard%202013.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "parked_vehicles": {
    "key": "parked_vehicles",
    "prompt": "Does the school have a policy to address vehicles parked for an extended period (e.g., reporting to security, local law enforcement, or tow company)?",
    "options": [
      {
        "value": "Very_Low",
        "label": "The school does not have a policy to address extended-stay vehicles parked onsite. A vehicle may remain parked on campus for an indefinite period of time without being reported."
      },
      {
        "value": "Medium",
        "label": "The school has written policies to address extended-stay vehicles. These policies have not been reviewed or shared externally. It is the responsibility of faculty and staff to identify and report extended-stay vehicles to school administrators."
      },
      {
        "value": "Very_High",
        "label": "The school has written policies to address extended-stay vehicles. They have shared the policy as needed (e.g., with local law enforcement). Security personnel patrol parking areas to identify and address extended-stay vehicles."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Develop policies to address extended-stay vehicles.",
      "Medium": "Coordinate policies that involve external reporting; for example, review the policy with local law enforcement or set up a contract with a towing company.",
      "Very_High": "Continue patrols and inspections. Maintain training for suspicious activity and search techniques."
    },
    "moreInfo": [
      {
        "title": "References",
        "body": [
          {
            "items": [
              {
                "text": "Lawrence J. Fennelly and Marianna A. Perry, The Handbook for School Safety and Security: Best Practices and Procedures (Amsterdam, The Netherlands: Elsevier 2014)."
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "DHS, Buildings and Infrastructure Protection Series: Primer to Design Safe School Projects in Case of Terrorist Attacks and School Shootings (FEMA-428/BIPS-07), January 2012, "
              },
              {
                "link": "https://www.dhs.gov/xlibrary/assets/st/bips07_428_schools.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "vehicular_control": {
    "key": "vehicular_control",
    "prompt": "Does the school control vehicular parking and circulation onsite?",
    "options": [
      {
        "value": "Very_Low",
        "label": "The school does not control access for any type of vehicle. Drivers can park any vehicle anywhere close to the school. Many vehicle access points are available."
      },
      {
        "value": "Low",
        "label": "The school does not control access for any type of vehicle. School buses have a separate, although wholly uncontrolled, loading and unloading zone."
      },
      {
        "value": "Medium",
        "label": "Some parking is monitored, but not by security personnel. School buses have separate loading and unloading zones. Signs, pavement markings, and/or orange cones are used to signify this separation, but some education and enforcement is also needed. Drivers cannot park vehicles closer than 70 feet away from the school."
      },
      {
        "value": "High",
        "label": "Security personnel monitor most parking. School buses have separate loading and unloading zones, and access to this area is effectively controlled. Signs, pavement markings, and/or orange cones are used to signify this separation, and enforcement is provided. Drivers cannot park vehicles closer than 70 feet away from the school."
      },
      {
        "value": "Very_High",
        "label": "Security personnel monitor all vehicle access. School buses have separate loading and unloading zones, and gates effectively control access to this area. Signs, pavement markings, and/or orange cones also may be used to signify this separation, and enforcement is provided. Drivers cannot park vehicles closer than 400 feet away from the school."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Review the access points to parking areas. Reduce the number of vehicle access points. Limit access through the use of curbs, fencing, gates, and a minimum number of entry points. Designate a separate area for bus traffic.",
      "Low": "Use signs, pavement markings, and/or orange cones to signify the separation between bus loading and unloading zones and other vehicular traffic. Designate separate visitor parking areas. Identify areas that allow vehicle placement within 70 feet of the school, and evaluate options to reduce the school's exposure to nearby vehicle placement.",
      "Medium": "Explore ways to employ security personnel to monitor most parking areas. Provide education and enforcement to restrict access to bus loading and unloading zones to buses only. Identify areas that allow vehicle placement within 400 feet of the school, and evaluate options to reduce the school's exposure to nearby vehicle placement.",
      "High": "Explore ways to increase monitoring capabilities for parking areas. Use gates to control access to bus loading and unloading areas, where possible. Identify areas that allow vehicle placement within 400 feet of the school, and evaluate options to reduce the school's exposure to nearby vehicle placement.",
      "Very_High": "Confirm the effectiveness of vehicle monitoring, and remedy any gaps or shortfalls. Continue efforts to separate bus traffic from all other vehicular traffic and to provide a minimum of 400 feet of standoff distance from the school building."
    },
    "moreInfo": [
      {
        "title": "References",
        "body": [
          {
            "separator": " ",
            "items": [
              {
                "text": "Barbara A. Nadel, Building Security: Handbook for Architectural Planning and Design (New York, NY: McGraw-Hill 2004)."
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "DHS, Buildings and Infrastructure Protection Series: Primer to Design Safe School Projects in Case of Terrorist Attacks and School Shootings (FEMA-428/BIPS-07), January 2012, "
              },
              {
                "link": "https://www.dhs.gov/xlibrary/assets/st/bips07_428_schools.pdf"
              }
            ]
          },
          {
            "items": [
              {
                "text": "New Jersey School Security Task Force, New Jersey School Security Task Force Report and Recommendations, July 2015, "
              },
              {
                "link": "http://www.state.nj.us/education/schools/security/TaskForceReport.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "high_speed_avenues": {
    "key": "high_speed_avenues",
    "prompt": "Does the school have a high-speed avenue(s) of approach?",
    "options": [
      {
        "value": "Very_Low",
        "label": "The school has at least one unmitigated high-speed avenue of approach. The school has made no efforts to attempt to mitigate any high-speed approaches."
      },
      {
        "value": "Low",
        "label": "The school has one or more high-speed avenue(s) of approach and has made some attempt to mitigate at least one of these vulnerabilities. The results of mitigation are ineffective. Materials are too weak or ineffective for some vehicles. No high-speed avenue of approach has been effectively mitigated."
      },
      {
        "value": "Medium",
        "label": "The school has one or more high-speed avenue(s) of approach and has made some attempt to mitigate at least one of these vulnerabilities. The results of mitigation are mixed. Spacing may be too wide, and materials may be too weak or ineffective for some vehicles. One approach may be effective in one area but not effective in another."
      },
      {
        "value": "High",
        "label": "The school has mitigated one or more high-speed avenue(s) of approach using effective and correctly placed bollards, barriers, or natural materials. Mitigation may include one or many types and combinations of bollards, barriers, or natural materials as long as they are effective at mitigating any and all high-speed avenue(s) of approach"
      },
      {
        "value": "Very_High",
        "label": "The school does not have a high-speed avenue of approach or has mitigated high-speed avenues of approach using effective and correctly placed bollards, barriers, or natural materials. Mitigation may include one or many types and combinations of bollards, barriers, or natural materials as long as they are effective at mitigating any and all high-speed avenues of approach."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Evaluate parking lots, and determine if long straight lines facilitate speeding. Consider whether parking lots could be reconfigured to reduce vulnerabilities. Install speed bumps to slow traffic.",
      "Low": "Conduct a barrier survey to evaluate effectiveness. Remedy large gaps between barriers, and replace barriers made of weak or ineffective materials.",
      "Medium": "Conduct a barrier survey to evaluate effectiveness. Remedy large gaps between barriers, and replace barriers made of weak or ineffective materials. Ensure the most critical high-speed avenue of approach is mitigated.",
      "High": "Prioritize unmitigated high-speed avenues of approach, and begin efforts to mitigate these vulnerabilities, beginning with the most critical.",
      "Very_High": "Maintain barriers, especially those made of natural materials. When construction or modification activities take place onsite, assess and address any new high-speed avenues of approach."
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "items": [
              {
                "text": "A high-speed avenue of approach is a road or flat area that would allow a vehicle to gain sufficient speed to crash into the school before it can be detected, deterred, or interdicted (stopped). A high-speed avenue of approach generally must lead to a critical or heavily populated area: if a road or flat area simply leads to a generally unoccupied corner of a brick building, it may not be considered a high-speed avenue of approach."
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "separator": " ",
            "items": [
              {
                "text": "DHS, Buildings and Infrastructure Protection Series: Primer to Design Safe School Projects in Case of Terrorist Attacks and School Shootings (FEMA-428/BIPS-07), January 2012, accessed April 26, 2018,  "
              },
              {
                "link": "https://www.dhs.gov/xlibrary/assets/st/bips07_428_schools.pdf"
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "Johns Hopkins University Applied Physics Laboratory, A Comprehensive Report on School Safety Technology, prepared for DOJ NIJ, October 2016, "
              },
              {
                "link": "https://www.ncjrs.gov/pdffiles1/nij/grants/250274.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "minimum_standoff_distance": {
    "key": "minimum_standoff_distance",
    "prompt": "What is the minimum standoff distance between the school and a vehicle?",
    "options": [
      {
        "value": "Very_Low",
        "label": "The school has an unmitigated standoff distance of less than 70 feet."
      },
      {
        "value": "Low",
        "label": "Some areas have a standoff distance of 70 feet or more, but the configuration has gaps."
      },
      {
        "value": "Medium",
        "label": "The school has effectively enforced standoff distance by more than 70 feet."
      },
      {
        "value": "High",
        "label": "The school has effectively enforced standoff distance by more than 70 feet but less than 400 feet."
      },
      {
        "value": "Very_High",
        "label": "The standoff distance from the school is at least 400 feet. Effective barriers enforce the standoff distance."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Allow only faculty and staff parking near the school building, and restrict visitor or general public parking to 70 or more feet away. Restrict parking between individual school buildings. At small standoff distances, even a few feet make a large difference.",
      "Low": "Explore ways to prevent any vehicles from parking within 70 feet of the school building. Refer to Buildings and Infrastructure Protection Series: Primer to Design Safe School Projects in Case of Terrorist Attacks and School Shootings (FEMA-428/BIPS-07) for more information.",
      "Medium": "Explore ways to increase the standoff distance from the school building. Refer to Buildings and Infrastructure Protection Series: Primer to Design Safe School Projects in Case of Terrorist Attacks and School Shootings (FEMA-428/BIPS-07) for more information.",
      "High": "Explore ways to increase the standoff from the school building to at least 400 feet. Refer to Buildings and Infrastructure Protection Series: Primer to Design Safe School Projects in Case of Terrorist Attacks and School Shootings (FEMA-428/BIPS-07) for more information.",
      "Very_High": "Provide camera coverage any areas where vehicles may be place (e.g., parent drop-off and pick-up areas, bus loading and unloading zones, parking lots). Ensure the school has adequate lighting capable of displaying and videotaping lot activity."
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "items": [
              {
                "text": "Standoff distances refer to the space between the building exterior to the nearest point that an explosive device can approach from any side."
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "separator": " ",
            "items": [
              {
                "text": "DHS, Buildings and Infrastructure Protection Series: Primer to Design Safe School Projects in Case of Terrorist Attacks and School Shootings (FEMA-428/BIPS-07), January 2012, accessed April 26, 2018,  "
              },
              {
                "link": "https://www.dhs.gov/xlibrary/assets/st/bips07_428_schools.pdf"
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "Johns Hopkins University Applied Physics Laboratory, A Comprehensive Report on School Safety Technology, prepared for DOJ NIJ, October 2016, "
              },
              {
                "link": "https://www.ncjrs.gov/pdffiles1/nij/grants/250274.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "interior_doors": {
    "key": "interior_doors",
    "prompt": "Does the construction of interior doors deter or delay an attack?",
    "options": [
      {
        "value": "Very_Low",
        "label": "Interior doors are primarily wood with glass windows or all-glass doors. The glass covers about half or more of the door. The school may have some steel doors. "
      },
      {
        "value": "Medium",
        "label": "The school has a mix of interior doors. While many are steel with minimal glass, some are wood or steel with extensive glass. The school may have steel doors without glass."
      },
      {
        "value": "Very_High",
        "label": "All interior doors are steel with at most a small skylight of reinforced glass."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Where possible, eliminate wood doors that have a large glass opening and all-glass doors. Replace with wood or steel doors with minimal glass. In some cases, it may be possible to retrofit glass with bullet-resistant glass.",
      "Medium": "Where possible, eliminate large glass openings. In some cases, it may be possible to retrofit with bullet-resistant glass.",
      "Very_High": "The use of this type of door may aid in protection for certain attacks but may create a vulnerability by limiting site lines and observation. Consider the tradeoffs and threats to the facility."
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "items": [
              {
                "text": "To deter is to discourage an action or prevent an occurrence. To delay is to slow down an intruder enough to force them to give up or to provide enough time for an effective response."
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "items": [
              {
                "text": "DHS, Buildings and Infrastructure Protection Series: Primer to Design Safe School Projects in Case of Terrorist Attacks and School Shootings (FEMA-428/BIPS-07), January 2012, accessed April 26, 2018,  "
              },
              {
                "link": "https://www.dhs.gov/xlibrary/assets/st/bips07_428_schools.pdf"
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "National Institute of Building Sciences, National Clearinghouse for Educational Facilities, Door Locking Options in Schools, March 2009, "
              },
              {
                "link": "https://files.eric.ed.gov/fulltext/ED508013.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "exterior_windows": {
    "key": "exterior_windows",
    "prompt": "Does the construction of exterior windows deter or delay an attack?",
    "options": [
      {
        "value": "Very_Low",
        "label": "Exterior windows are tempered glass and can be opened. The windows are large enough that an adult could climb through them when open."
      },
      {
        "value": "Medium",
        "label": "Most exterior windows are small (i.e., a teenager cannot climb through) and not operational. The school has some exterior operational windows that are larger."
      },
      {
        "value": "Very_High",
        "label": "The school has no exterior windows, or the exterior windows are tall and narrow. They are too small to allow anyone to pass through them. They cannot be opened and use reinforced glass."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Train faculty, staff, and students to avoid window or glass areas during an attack. Replacing windows with smaller, nonoperational windows is an expensive option and may not be possible. If that is the case, close and lock windows at all times. If the school requires windows to be open for ventilation, be sure everyone knows how to close and lock a door in an emergency. Other possibilities include installing heavy window coverings that can be lowered or slide on tracks and that can be positioned quickly or even remotely. This may provide some concealment from an attacker.",
      "Medium": "Train faculty, staff, and students to avoid window or glass areas during an attack. Where possible, replace large operational windows. If this is not feasible, install heavy window coverings that can be lowered or slide on tracks and can be positioned quickly or even remotely. This may provide some concealment from an attacker. Ensure that everyone (including staff, faculty, and students) knows how to close and lock the windows.",
      "Very_High": "Train faculty, staff, and students to avoid window or glass areas during an attack."
    },
    "moreInfo": [
      {
        "title": "References",
        "body": [
          {
            "separator": " ",
            "items": [
              {
                "text": "DHS, Buildings and Infrastructure Protection Series: Primer to Design Safe School Projects in Case of Terrorist Attacks and School Shootings (FEMA-428/BIPS-07), January 2012, accessed April 26, 2018,  "
              },
              {
                "link": "https://www.dhs.gov/xlibrary/assets/st/bips07_428_schools.pdf"
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "Johns Hopkins University Applied Physics Laboratory, A Comprehensive Report on School Safety Technology, prepared for DOJ NIJ, October 2016, "
              },
              {
                "link": "https://www.ncjrs.gov/pdffiles1/nij/grants/250274.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "utilities": {
    "key": "utilities",
    "prompt": "Are utilities (electric, gas, communications) protected or secured against tampering?",
    "options": [
      {
        "value": "Very_Low",
        "label": "The gas meter(s) are exposed with no bollards protecting them. The primary electric switch box or transformer to the school is exposed and is not protected by a fence or similar enclosure. The transformer may be located adjacent to the building or in an open area near the school. Communication lines are exposed, easily identified, and accessible."
      },
      {
        "value": "Medium",
        "label": "The gas meter(s) are exposed but may have bollards protecting them. The primary electric switch box or transformer to the school is exposed and is not protected by a fence or similar enclosure. The transformer may be located adjacent to the building or in an open area near the school."
      },
      {
        "value": "Very_High",
        "label": "The gas meter(s) are either secured inside the building or exposed but protected by bollards and/or fence. The transformer or main electric switch box is secured inside the building or exposed but fenced and difficult to access. Communication demarcation points are secured inside the building or exposed but fenced and difficult to access."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Install bollards around gas meter and transformers. Restrict access through the use of fence material to communication lines or communication boxes. If the school has portable buildings, the options may be more limited, and these areas may need additional surveillance.",
      "Medium": "Where possible, install fence material around the electric transformers and communication boxes to delay access to the equipment. Evaluate the internal demarcation points and ensure they are in secured rooms. If the school has portable buildings, the options may be more limited and these areas may need additional surveillance.",
      "Very_High": "If the school has portable buildings, the options may be more limited and these areas may need additional surveillance."
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "items": [
              {
                "text": "An attacker may attempt to damage utility assets to create diversions or complicate response efforts."
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "separator": " ",
            "items": [
              {
                "text": "DHS, Buildings and Infrastructure Protection Series: Primer to Design Safe School Projects in Case of Terrorist Attacks and School Shootings (FEMA-428/BIPS-07), January 2012, accessed April 26, 2018,  "
              },
              {
                "link": "https://www.dhs.gov/xlibrary/assets/st/bips07_428_schools.pdf"
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "Connecticut Department of Administrative Services, Report of the School Safety Infrastructure Council, FORM SCG-7000, November 19, 2015, "
              },
              {
                "link": "http://portal.ct.gov/-/media/DAS/Office-of-School-Construction-Grants/Task-188---Required-Forms-Regarding-Plan-Review-and-Approval/FORM-SCG-7000-SSIC-Report-Nov-19-2015.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "ceilings": {
    "key": "ceilings",
    "prompt": "Are the ceilings inside the building enclosed (drywall or similar) or exposed beam?",
    "options": [
      {
        "value": "Very_Low",
        "label": "Many ceilings inside the building use ceiling tiles that can be accessed easily by standing on a chair or desk or ladder or even lifting a person. Ceiling tiles exist in isolated areas of the school such as restrooms, library, and storage rooms."
      },
      {
        "value": "Medium",
        "label": "Most ceilings are open beam or enclosed (i.e., with drywall or similar material). A few places such as restrooms and storage rooms have ceiling tiles."
      },
      {
        "value": "Very_High",
        "label": "All ceilings are exposed beam. No ceiling tiles are in use."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Wherever possible, eliminate ceiling tiles and replace with open beam or solid surface (e.g., drywall) to eliminate hiding places of contraband including weapons. In areas where not practical to eliminate, establish a process of inspection or monitor and record area with cameras and review video for suspicious activity.",
      "Medium": "Wherever possible, eliminate ceiling tiles and replace with open beam or solid surface (e.g., drywall) to eliminate hiding places of contraband including weapons. Camera surveillance may not be practical in these areas, so establish a process for routine inspection.",
      "Very_High": "Continue a high level of surveillance, and watch for unusual activity and items."
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "items": [
              {
                "text": "Rooms or areas without ceilings or ceiling tiles minimize the ability to hide weapons or other contraband. While it may be impractical for all ceilings to be open, school officials may consider minimizing the use of ceiling tiles, using smaller vents where possible, and frequently inspecting for tampering or intrusion."
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "separator": " ",
            "items": [
              {
                "text": "DHS, Buildings and Infrastructure Protection Series: Primer to Design Safe School Projects in Case of Terrorist Attacks and School Shootings (FEMA-428/BIPS-07), January 2012, accessed April 26, 2018,  "
              },
              {
                "link": "https://www.dhs.gov/xlibrary/assets/st/bips07_428_schools.pdf"
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "National Institute of Building Sciences, National Clearinghouse for Educational Facilities, Low-Cost Security Measures for School Facilities, April 2008, "
              },
              {
                "link": "https://files.eric.ed.gov/fulltext/ED507917.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "hidden_areas": {
    "key": "hidden_areas",
    "prompt": "Do hallways, stairwells, and common areas have hidden areas that are hard to observe from classrooms or doorways?",
    "options": [
      {
        "value": "Very_Low",
        "label": "Some stairwells are narrow and provide hiding areas under the steps. Some hallways are too narrow for the school population, and often it is hard to observe activity in the hallways. Some common areas are irregularly shaped and provide hiding areas."
      },
      {
        "value": "Medium",
        "label": "Most stairwells and hallways are wide enough to accommodate during the busiest times of day. Only a few isolated hiding areas exist under stairwells. Common areas generally are open and observable with just isolated areas that cannot be easily observed."
      },
      {
        "value": "Very_High",
        "label": "Stairwells are wide and open and are the same width as the hallways to prevent bottlenecks. The hallways are wide enough, even during busiest times, that almost all hidden areas are eliminated. Common areas are well-designed, and all areas can be observed without obstruction."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Identify and document hiding areas or areas that have a poor line of sight and are hard to observe. Establish a process of routine inspection. When possible, post faculty or staff near these locations during student class changes. Watch for unusual activity such as hiding of backpacks, weapons, and contraband or a cluster of students obviously concealing activities.",
      "Medium": "Wherever possible, add electronic surveillance to these areas (e.g., cameras). Other options include posting faculty and staff near these locations during student class changes. Watch for unusual activity such as hiding of backpacks, weapons, and contraband, or a cluster of students obviously concealing activities.",
      "Very_High": "Continue a high level of surveillance, and watch for unusual activity and items."
    },
    "moreInfo": [
      {
        "title": "References",
        "body": [
          {
            "separator": " ",
            "items": [
              {
                "text": "DHS, Buildings and Infrastructure Protection Series: Primer to Design Safe School Projects in Case of Terrorist Attacks and School Shootings (FEMA-428/BIPS-07), January 2012, accessed April 26, 2018,  "
              },
              {
                "link": "https://www.dhs.gov/xlibrary/assets/st/bips07_428_schools.pdf"
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "Heather L. Schwartz, et al., The Role of Technology in Improving K-12 School Safety, DOJ NIJ, January 2016, "
              },
              {
                "link": "https://www.rand.org/content/dam/rand/pubs/research_reports/RR1400/RR1488/RAND_RR1488.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "building_objects": {
    "key": "building_objects",
    "prompt": "Is the exterior of the building free of objects that could be used for hiding contraband or for climbing to other floors or the roof?",
    "options": [
      {
        "value": "Very_Low",
        "label": "Trees, shrubs, portable buildings, unsecured ladders, or playground equipment exist at numerous locations around the school. Many of these locations provide hiding areas for contraband. Some areas can be accessed and then used to climb to the roof or other floors of the school. If the school has portable buildings, some of these are near tree lines or fence lines and can easily be accessed. Also, the area under the portable building has hiding spaces."
      },
      {
        "value": "Medium",
        "label": "Most of the area next to the school is free of objects, which minimizes potential areas of concealment. The area has a few shrubs or trees. If the school has portable buildings, some of these are near tree lines or fence lines and can easily be accessed. The areas under the portable buildings are fenced, blocked, or otherwise not accessible."
      },
      {
        "value": "Very_High",
        "label": "The area next to the school building is free of objects. The exterior is clear of obstructions other than fenced areas near trash bins, utilities, or loading docks. (Note: This type of barren exterior leaves people fleeing the school building during an active-shooter incident exposed and without cover, making them more vulnerable to gunfire.) The school does not have portable buildings."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "To the extent possible, ensure the exterior of the school is free of objects and areas that allow access to the roof or other floors of the building. This may require removing trees, relocating storage sheds or portable buildings, or other steps to prevent access. In some cases, shrubs or hedges next to the building or under window openings are acceptable if they are a thorny type of plant that would discourage anyone from hiding themselves or contraband. If items cannot be moved or secured, establish routine observation and inspection of the area.",
      "Medium": "To the extent possible, ensure the exterior of the school is free of objects and areas that allow access to the roof or other floors of the building. (Note: This type of barren exterior leaves people fleeing the school building during an active-shooter incident exposed and without cover, making them more vulnerable to gunfire.) In some cases, shrubs or hedges next to the building or under window openings are acceptable and add to security if they are a thorny type of plant that would discourage anyone from hiding themselves or contraband.",
      "Very_High": "If present, regularly assess and maintain the shrubs to ensure their height balances aesthetics with security."
    },
    "moreInfo": [
      {
        "title": "References",
        "body": [
          {
            "separator": " ",
            "items": [
              {
                "text": "Barbara A. Nadel, Building Security: Handbook for Architectural Planning and Design (New York, NY: McGraw-Hill 2004)."
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "DHS, Buildings and Infrastructure Protection Series: Primer to Design Safe School Projects in Case of Terrorist Attacks and School Shootings (FEMA-428/BIPS-07), January 2012, "
              },
              {
                "link": "https://www.dhs.gov/xlibrary/assets/st/bips07_428_schools.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "fire_alarm_pull_stations": {
    "key": "fire_alarm_pull_stations",
    "prompt": "Are fire alarm pull stations protected from misuse?",
    "options": [
      {
        "value": "Very_Low",
        "label": "Fire alarm pull stations are unprotected from misuse, and there has been no discussion or action regarding ways to protect this equipment. Activation of the fire alarm system may be used as a way to move students out of their classrooms and into open areas as part of an attack."
      },
      {
        "value": "Medium",
        "label": "Fire alarm pull stations have protective covers, but they may be located in isolated areas."
      },
      {
        "value": "Very_High",
        "label": "Fire alarm pull stations have protective covers and are located in areas that allow for unobstructed surveillance."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Consult with the fire marshal to ensure any changes to the fire alarm system are permitted by code. If possible, provide protective covers to deter tampering and reduce the risk of misuse without restricting legitimate use in the event of an actual fire. Protective covers often emit loud warning sounds when lifted to draw attention to the location where the fire alarm has been activated.",
      "Medium": "Relocate fire alarm pull stations to areas that allow for unobstructed surveillance. Isolated equipment is more susceptible to unmonitored activation, which could be used as a distraction or an attack initiation signal.",
      "Very_High": "(1) Randomly check fire alarms for tampering. (2) In consultation with the fire marshal, explore the option of removing fire alarm pull stations, except for one pull station at a central location. If this option is employed, the pull stations must be removed and not simply disconnected."
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "items": [
              {
                "text": "It is not possible to control access to fire alarm pull stations. Covers simply provide protection against accidental misuse or mischief. A determined attacker will be able to bypass a protective covering."
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "separator": " ",
            "items": [
              {
                "text": "DHS, Buildings and Infrastructure Protection Series: Primer to Design Safe School Projects in Case of Terrorist Attacks and School Shootings (FEMA-428/BIPS-07), January 2012, accessed April 26, 2018,  "
              },
              {
                "link": "https://www.dhs.gov/xlibrary/assets/st/bips07_428_schools.pdf"
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "Illinois Terrorism Task Force, School Safety Working Group, Recommendations of the Illinois Terrorism Task Force School Safety Working Group, April 5, 2018, "
              },
              {
                "link": "https://www.iasaedu.org/cms/lib/IL01923163/Centricity/Domain/4/ITTF%20School%20Safety%20Working%20Group%20Recommendations%20-%20Final%20draft%20040318.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "camera_system": {
    "key": "camera_system",
    "prompt": "Does the school have a camera system?",
    "options": [
      {
        "value": "Very_Low",
        "label": "The school has no camera system in place."
      },
      {
        "value": "Low",
        "label": "The school has cameras, but coverage of sensitive areas is minimal."
      },
      {
        "value": "Medium",
        "label": "Cameras are in place and effective in most areas. Some entrances and sensitive areas may lack coverage."
      },
      {
        "value": "High",
        "label": "Cameras cover most areas of the school, including entrances and sensitive areas."
      },
      {
        "value": "Very_High",
        "label": "Cameras cover most areas of the school, including entrances and sensitive areas."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Explore the option of installing a camera system onsite. If this undertaking is feasible and appropriate for the school, install cameras throughout the building to enable staff to identify and assess threats.",
      "Low": "Conduct a camera system survey to identify areas where coverage is lacking. Update the system to increase coverage.",
      "Medium": "Conduct a camera system survey to identify areas where coverage may be lacking. Ensure all entrances and sensitive areas have camera coverage.",
      "High": "Conduct a camera system survey to identify any areas where coverage may still be lacking.",
      "Very_High": "Conduct a camera system survey to confirm camera coverage is sufficient."
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "items": [
              {
                "text": "A camera system survey is conducted to determine if the design and use of the CCV/VSS is appropriate given the school’s security needs, the intended purpose of the camera system, and for the given circumstances."
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "items": [
              {
                "text": "Mary Lynn Garcia, The Design and Evaluation of Physical Protection Systems, 2nd Edition (Burlington, MA: Elsevier 2008)."
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "DHS, CCTV Technology Handbook, July 2013,"
              },
              {
                "link": "https://www.dhs.gov/sites/default/files/publications/CCTV-Tech-HBK_0713-508.pdf"
              }
            ]
          },
          {
            "items": [
              {
                "text": "DHS, Buildings and Infrastructure Protection Series: Primer to Design Safe School Projects in Case of Terrorist Attacks and School Shootings (FEMA-428/BIPS-07), January 2012, "
              },
              {
                "link": "https://www.dhs.gov/xlibrary/assets/st/bips07_428_schools.pdf"
              }
            ]
          },
          {
            "items": [
              {
                "text": "Johns Hopkins University Applied Physics Laboratory, A Comprehensive Report on School Safety Technology, prepared for DOJ NIJ, October 2016, "
              },
              {
                "link": "https://www.ncjrs.gov/pdffiles1/nij/grants/250274.pdf"
              }
            ]
          },
          {
            "items": [
              {
                "text": "Dean Drako, School Security Camera System Report, 2016, "
              },
              {
                "link": "https://www.eagleeyenetworks.com/school-security-camera-system-report"
              }
            ]
          }
        ]
      }
    ]
  },
  "camera_type": {
    "key": "camera_type",
    "prompt": "Does the system use an effective combination of camera types? ",
    "options": [
      {
        "value": "Very_Low",
        "label": "The camera system typically uses a single fixed camera to provide coverage for each entrance."
      },
      {
        "value": "Medium",
        "label": "Some cameras may not be effective under some lighting conditions. Some cameras may not be the best choice for a given application."
      },
      {
        "value": "Very_High",
        "label": "An effective mix of camera types is used and accounts for changes in illumination."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Conduct a camera system survey to identify areas where cameras are ineffective. Based on the results of the survey, perform system updates.",
      "Medium": "Conduct a camera system survey to identify areas where cameras are ineffective. Based on the results of the survey, perform system updates.",
      "Very_High": "Conduct a camera system survey to confirm the effectiveness of each camera in use. Confirm that area lighting is compatible with each camera."
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "items": [
              {
                "text": "Skip this question if your school does not have a camera system. Fixed cameras are mounted in a stationary position and typically focused on one particular area of interest. Fixed cameras generally are less expensive than pan-tilt-zoom (PTZ) cameras and require less maintenance since they have fewer moving parts. PTZ cameras typically can be turned and tilted on two axes (i.e., up and down, side to side). PTZ cameras offer more flexibility for viewing and capturing images in real time than fixed cameras. PTZ cameras can be operated manually or in an automatic scan mode. CCV/VSS design considerations must include lighting, since illumination levels affect system requirements. Exterior cameras often require lenses with automatic apertures to compensate for changes in light levels. Interior cameras may require internal software to compensate for backlight (i.e., the contrast between low interior light levels and high exterior daytime light levels)."
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "items": [
              {
                "text": "(1)"
              },
              {
                "text": "DHS, CCTV Technology Handbook, July 2013, accessed April 26, 2018, "
              },
              {
                "link": "https://www.dhs.gov/sites/default/files/publications/CCTV-Tech-HBK_0713-508.pdf"
              }
            ]
          },
          {
            "items": [
              {
                "text": "(2)"
              },
              {
                "text": "Mary Lynn Garcia, The Design and Evaluation of Physical Protection Systems, 2nd Edition (Burlington, MA: Elsevier 2008)."
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "Johns Hopkins University Applied Physics Laboratory, A Comprehensive Report on School Safety Technology, prepared for DOJ NIJ, October 2016, "
              },
              {
                "link": "https://www.ncjrs.gov/pdffiles1/nij/grants/250274.pdf"
              }
            ]
          },
          {
            "items": [
              {
                "text": "Tod Schneider, School Security Technologies, National Institute of Building Sciences, National Clearinghouse for Educational Facilities, January 2010, "
              },
              {
                "link": "https://files.eric.ed.gov/fulltext/ED507917.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "system_monitored": {
    "key": "system_monitored",
    "prompt": "Is the system monitored? ",
    "options": [
      {
        "value": "Very_Low",
        "label": "No real-time monitoring of the camera system occurs."
      },
      {
        "value": "Low",
        "label": "Untrained staff may look at the CCV/VSS monitors on occasion or when notified of an incident. They are usually focused on their other primary duties."
      },
      {
        "value": "Medium",
        "label": "Trained staff members monitor the CCV/VSS and do not have additional duties that could distract them from monitoring the system. However, they must follow too many screens at once (typically more than eight). They do not receive regular breaks, which impacts their ability to monitor multiple camera feeds effectively."
      },
      {
        "value": "High",
        "label": "Trained, dedicated staff members monitor the CCV/VSS and are not overwhelmed by too many screens (typically eight or less). No single person monitors the camera system for an extended period of time (more than 15 minutes)."
      },
      {
        "value": "Very_High",
        "label": "Trained, dedicated staff members effectively monitor the CCV/VSS. Monitoring staff frequently rotate shifts (every 10-15 minutes), and the number of cameras each staff member monitors is limited to eight or fewer. The system is equipped with capabilities that aid in the determination of suspicious activity."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Employ staff to monitor the camera system. If possible, provide a camera monitor in the front office, visible to both staff and visitors.",
      "Low": "Provide training to monitoring staff, and reduce or, if possible, eliminate their other duties.",
      "Medium": "Explore options to maximize the effectiveness of monitoring and observation, such as frequently rotating shifts for staff and limiting the number of cameras each staff member monitors.",
      "High": "Explore the feasibility of procuring a software program that aids in the determination of suspicious activity.",
      "Very_High": "Explore the options of providing local law enforcement with the capability to access and monitor the camera system. Law enforcement personnel may easily view Internet protocol cameras remotely."
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "items": [
              {
                "text": "Skip this question if your school does not have a camera system."
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "separator": " ",
            "items": [
              {
                "text": "(1)"
              },
              {
                "text": "DHS, Buildings and Infrastructure Protection Series: Primer to Design Safe School Projects in Case of Terrorist Attacks and School Shootings (FEMA-428/BIPS-07), January 2012, accessed April 24, 2018, "
              },
              {
                "link": "https://www.dhs.gov/xlibrary/assets/st/bips07_428_schools.pdf"
              }
            ]
          },
          {
            "separator": " ",
            "items": [
              {
                "text": "(2)"
              },
              {
                "text": "Johns Hopkins University Applied Physics Laboratory, A Comprehensive Report on School Safety Technology, prepared for DOJ NIJ, October 2016, accessed April 26, 2018, "
              },
              {
                "link": "https://www.ncjrs.gov/pdffiles1/nij/grants/250274.pdf"
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "DHS, CCTV Technology Handbook, July 2013,"
              },
              {
                "link": "https://www.dhs.gov/sites/default/files/publications/CCTV-Tech-HBK_0713-508.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "recorded_and_reviewed": {
    "key": "recorded_and_reviewed",
    "prompt": "Is information recorded and reviewed?",
    "options": [
      {
        "value": "Very_Low",
        "label": "The school does not record information that the CCV/VSS captures."
      },
      {
        "value": "Medium",
        "label": "Personnel review recorded information only after an incident. The storage capability is limited to a week or less, so many events are not available after the fact."
      },
      {
        "value": "Very_High",
        "label": "The school stores recorded information for at least a month. Personnel review information for suspicious activity, not only after an incident."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Install video recording and storage systems. Develop a policy for the review of recorded information (e.g., periodically or only after an incident). Recorded information can support investigations.",
      "Medium": "Increase the camera system's storage capacity, preferably to a month.",
      "Very_High": "Increase the camera system's storage capacity to a minimum of 90 days."
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "items": [
              {
                "text": "Skip this question if your school does not have a camera system."
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "separator": " ",
            "items": [
              {
                "text": "(1)"
              },
              {
                "text": "Mary Lynn Garcia, Vulnerability Assessment of Physical Protection Systems (Burlington, MA: Elsevier 2006); "
              }
            ]
          },
          {
            "separator": " ",
            "items": [
              {
                "text": "(2)"
              },
              {
                "text": "David G. Patterson, Implementing Physical Protection Systems: A Practical Guide (Alexandria, VA: ASIS International 2013)."
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "Pennsylvania State Police, Domestic Security Division, Risk and Vulnerability Assessment Team, School Safety Report, 2012, "
              },
              {
                "link": "http://www.psp.pa.gov/public-safety/Documents/RVAT%20School%20Safety%20Report_FINAL%20v1.2.pdf"
              }
            ]
          }
        ]
      }
    ]
  },
  "overall_condition": {
    "key": "overall_condition",
    "prompt": "What is the overall condition of the camera system?",
    "options": [
      {
        "value": "Very_Low",
        "label": "The CCV/VSS needs maintenance and updates. The system lacks backup power."
      },
      {
        "value": "Medium",
        "label": "The CCV/VSS is in good condition, but some cameras are inoperable, even in key locations. Maintenance or repair is contracted work. The system has backup power."
      },
      {
        "value": "Very_High",
        "label": "All cameras are in good working condition. Continuous updates occur on the CCV/VSS, and it is routinely tested. Maintenance or repair, when needed, is performed “in house.” The system has backup power."
      }
    ],
    "thingsToConsider": {
      "Very_Low": "Perform maintenance and system updates. Repair or replace any inoperable cameras, especially those in key locations. Explore options to provide backup power for the camera system.",
      "Medium": "Repair or replace any inoperable cameras. Ensure continuous updates are applied to the system, and test it routinely.",
      "Very_High": "Evaluate the comprehensiveness of camera system tests. Testing should ensure the cameras work properly and should include an assessment of camera views. It may include switching the system to operate on backup power."
    },
    "moreInfo": [
      {
        "title": "Background",
        "body": [
          {
            "items": [
              {
                "text": "Skip this question if your school does not have a camera system."
              }
            ]
          }
        ]
      },
      {
        "title": "References",
        "body": [
          {
            "separator": " ",
            "items": [
              {
                "text": "ASIS International, Protection of Assets: Physical Security (Alexandria, VA: ASIS International, 2012)."
              }
            ]
          }
        ]
      },
      {
        "title": "For More Information",
        "body": [
          {
            "items": [
              {
                "text": "DHS, CCTV Technology Handbook, July 2013, "
              },
              {
                "link": "https://www.dhs.gov/sites/default/files/publications/CCTV-Tech-HBK_0713-508.pdf"
              }
            ]
          },
          {
            "items": [
              {
                "text": "DHS, Buildings and Infrastructure Protection Series: Primer to Design Safe School Projects in Case of Terrorist Attacks and School Shootings (FEMA-428/BIPS-07), January 2012, "
              },
              {
                "link": "https://www.dhs.gov/xlibrary/assets/st/bips07_428_schools.pdf"
              }
            ]
          },
          {
            "items": [
              {
                "text": "Johns Hopkins University Applied Physics Laboratory, A Comprehensive Report on School Safety Technology, prepared for DOJ NIJ, October 2016, "
              },
              {
                "link": "https://www.ojp.gov/pdffiles1/nij/grants/250274.pdf"
              }
            ]
          }
        ]
      }
    ]
  }
};

